import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { api } from '@/lib/api';
import { getBranding, getBrandName } from '@/lib/branding';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Command, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem } from '@/components/ui/command';
import { FilterFieldPicker } from '@/components/report-builder/FilterFieldPicker';
import { useGlobalShortcut } from '@/hooks/useGlobalShortcut';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ArrowLeft, Pencil, Loader2, MessageSquare, X, Filter, Lock, Bell, Home, Share2, BarChart2, Sparkles, Download, CalendarClock, FileText, Image as ImageIcon, RefreshCw } from 'lucide-react';
import { Progress } from '@/components/ui/progress';
import { ChartRenderer } from '@/components/report-builder/ChartRenderer';
import { TableRenderer } from '@/components/report-builder/TableRenderer';
import { PivotTableRenderer } from '@/components/report-builder/PivotTableRenderer';
import { ComparisonTableRenderer } from '@/components/report-builder/ComparisonTableRenderer';
import { LossTriangleRenderer } from '@/components/report-builder/LossTriangleRenderer';
import { SlicerControl } from '@/components/report-builder/SlicerControl';
import { SavedFilterViews } from '@/components/report-builder/SavedFilterViews';
import { getSlicerSetting, sortSlicerValues, resolveDefaultSelection, writeRememberedSelection } from '@/lib/slicerSettings';
import { isObjectVisual } from '@/components/report-builder/FreeVisual';
import { StartDiscussionDialog, DiscussionThreadDialog, DiscussionListDialog } from '@/components/DiscussionDialog';
import ObjectChat from '@/components/ObjectChat';
import ShareViewDialog from '@/components/ShareViewDialog';
import PresenceViewers from '@/components/PresenceViewers';
import { connectMessaging, setViewing, broadcastNav, broadcastFollowCursor, onMessagingEvent, startFollow, stopFollow } from '@/lib/messagingSocket';
import { Radio, Users, MousePointer2 } from 'lucide-react';
import { emitCoachEvent } from '@/coach/coachApi';
import { onCoachAction } from '@/coach/coachActionBus';
import { InvestigatePanel } from '@/components/investigate/InvestigatePanel';
import { ResponsiveGridLayout } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import { PageNavChrome, isPageVisible, normalizeNavStyle } from '@/components/dashboards/PageNavChrome';
import 'react-resizable/css/styles.css';
import { DataQualityBanner } from '@/components/DataQualityBanner';
import { getFieldLabel, resolveLabel, entityQualifiedLabel } from '@/lib/fieldLabel';
import { SharePreviewDialog } from '@/components/SharePreviewDialog';
import ExportScheduleDialog from '@/components/ExportScheduleDialog';
import ExportFrameworkDialog from '@/components/ExportFrameworkDialog';
import { runVisualQuery, filtersForSelection } from '@/lib/visualQueryEngine';
import { isDateField } from '@/lib/dateSlicerUtils';
import { safeEvalMath, withMeasureUnits, createFormatValue } from '@/components/report-builder/formatUtils';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import AssetUsageTab from '@/components/AssetUsageTab';
import { TileMeasureControls } from '@/components/report-builder/TileMeasureControls';
import { ReportSourceComparison } from '@/components/ReportSourceComparison';
import { ReportPagedViewer } from '@/components/report-builder/ReportPagedViewer';
import { FileStack, LayoutGrid, TrendingUp, ShieldCheck } from 'lucide-react';
import { ForecastChart, futureLabels } from '@/components/explore/ForecastChart';
import { ForecastTile } from '@/components/explore/ForecastTile';
import { SentinelSummaryTile } from '@/components/explore/SentinelSummaryTile';
import { useAuth } from '@/contexts/AuthContext';
import { useWorkspaceTitle, useWorkspace } from '@/contexts/WorkspaceContext';
// html2canvas loaded dynamically when needed

// html2canvas fails ("Could not export PDF") when a page contains a cross-origin
// image (brand logo, uploaded image, external chart art) because the rendered
// <canvas> becomes tainted and toDataURL() throws a SecurityError. Before
// capturing, inline every image as a same-origin data URL; any image that can't
// be fetched (no CORS) is hidden so it can't taint the canvas. Returns a restore
// fn to undo the DOM changes after capture.
async function inlineImagesForCapture(root) {
  if (!root) return () => {};
  const imgs = Array.from(root.querySelectorAll('img'));
  const restores = [];
  await Promise.all(imgs.map(async (img) => {
    const src = img.getAttribute('src') || '';
    if (!src || src.startsWith('data:')) return;
    try {
      const resp = await fetch(src, { credentials: 'include' });
      if (!resp.ok) throw new Error('bad status');
      const blob = await resp.blob();
      const dataUrl = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(blob);
      });
      restores.push([img, src, img.style.visibility]);
      img.setAttribute('src', dataUrl);
    } catch {
      restores.push([img, src, img.style.visibility]);
      img.style.visibility = 'hidden';
    }
  }));
  return () => restores.forEach(([img, s, vis]) => { img.setAttribute('src', s); img.style.visibility = vis; });
}

// Searchable "add filter" picker (grouped by source table) — shared with the
// Dashboard Builder's Slicers / Global-Filter shelves.


export default function ReportViewPage() {
  const navigate = useNavigate();
  const { user, hasPermission, permsLoaded } = useAuth();
  const me = user?.user_id;
  const canCertify = ['admin', 'super_admin', 'tenant_admin', 'steward', 'data_steward', 'owner'].includes(user?.role);
  const isAdmin = ['admin', 'super_admin', 'tenant_admin', 'owner'].includes(user?.role);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const printMode = searchParams.get('print') === '1';
  // Pixel-perfect dashboard export: render the on-screen dashboard grid (not a
  // paginated report) so the server-side Chromium capture matches what users see.
  const pixelMode = searchParams.get('pixel') === '1';
  // Chromeless (embedded, e.g. the Home dashboard): show ONLY the logo, the
  // dashboard name, the filters and the visuals — no back arrow, no action
  // buttons, no side panels.
  const chromeless = searchParams.get('chromeless') === '1';
  const refreshThumb = searchParams.get('refreshThumb') === '1';
  // Same component powers /reports/view and /dashboards/view — detect
  // which so the Usage sheet queries the right object_type.
  const assetType = location.pathname.startsWith('/dashboards') ? 'dashboard' : 'report';
  const reportId = searchParams.get('id');
  // Personal table layout prefs (column widths, header label/font, wrap/fit)
  // made while viewing — stored per-user in localStorage, not on the shared report.
  const [viewVizOverrides, setViewVizOverrides] = useState({});
  useEffect(() => {
    if (!reportId) return;
    try { const raw = localStorage.getItem(`inzara.tableprefs.${reportId}`); setViewVizOverrides(raw ? JSON.parse(raw) : {}); } catch { setViewVizOverrides({}); }
  }, [reportId]);
  const handleTablePref = useCallback((vid, cfg) => {
    setViewVizOverrides(prev => {
      const next = { ...prev, [vid]: cfg };
      try { localStorage.setItem(`inzara.tableprefs.${reportId}`, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  }, [reportId]);
  const discussionParam = searchParams.get('discussion');
  const viewStateParam = searchParams.get('viewState');
  const followParam = searchParams.get('follow');
  const followNameParam = searchParams.get('fn');
  const drillFilterParam = searchParams.get('drillFilter');
  const [drillThroughFilter, setDrillThroughFilter] = useState(null);
  const [report, setReport] = useState(null);
  const [compareOpen, setCompareOpen] = useState(false);
  // Label this insight's Workbench dock chip with its real name.
  useWorkspaceTitle(report?.name);
  const { closeTab } = useWorkspace();
  const canCompareSource = !!(report?.config?.import_session_id);
  useEffect(() => {
    if (canCompareSource && searchParams.get('compareSource') === '1') setCompareOpen(true);
  }, [canCompareSource, searchParams]);
  // Back arrow: CLOSE this report/dashboard (drop its workbench tab) and leave —
  // it should not linger in the workbench after the user backs out.
  const closeAndLeave = useCallback(() => {
    if (reportId) closeTab(`insight:${reportId}`);
    navigate('/dashboards');
  }, [reportId, closeTab, navigate]);
  const [allFields, setAllFields] = useState([]);
  const [fiscalStartMonth, setFiscalStartMonth] = useState(1);
  const [loading, setLoading] = useState(true);
  const [discussions, setDiscussions] = useState([]);
  const [discussionOpen, setDiscussionOpen] = useState(false);
  const [discussionCtx, setDiscussionCtx] = useState({});
  const [discussionScreenshot, setDiscussionScreenshot] = useState('');
  const [threadOpen, setThreadOpen] = useState(false);
  const [threadId, setThreadId] = useState(null);
  const [contextMenu, setContextMenu] = useState(null);
  const [investigateOpen, setInvestigateOpen] = useState(false);
  const [investigateTarget, setInvestigateTarget] = useState(null);
  const [listOpen, setListOpen] = useState(false);
  const [listDiscussions, setListDiscussions] = useState([]);
  const [discussionVisualId, setDiscussionVisualId] = useState('');
  const lastHoveredData = React.useRef(null);
  const [viewCorrPopup, setViewCorrPopup] = useState(null);
  const [rightPanelOpen, setRightPanelOpen] = useState(false);
  const [rightPanelTab, setRightPanelTab] = useState('chat');
  const [viewAlerts, setViewAlerts] = useState([]);
  const [viewSchedules, setViewSchedules] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [shareOpen, setShareOpen] = useState(false);
  const [usageOpen, setUsageOpen] = useState(false);
  const [themeLogo, setThemeLogo] = useState('');
  const [themeLogoPos, setThemeLogoPos] = useState('left');
  const [exporting, setExporting] = useState(false);
  // Determinate export progress + cooperative cancellation (no page reload).
  const [exportProgress, setExportProgress] = useState({ current: 0, total: 0, label: '' });
  const exportCancelRef = useRef(false);
  const exportAbortRef = useRef(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [frameworkExportOpen, setFrameworkExportOpen] = useState(false);
  const [exportPageIds, setExportPageIds] = useState(null); // null = current page only
  const [exportPickerOpen, setExportPickerOpen] = useState(false);
  const [exportFormat, setExportFormat] = useState('pdf'); // 'pdf' | 'png'
  // Report-layout page-range export (chosen by the reader).
  const [rangeTotal, setRangeTotal] = useState(0);
  const [rangeFrom, setRangeFrom] = useState(1);
  const [rangeTo, setRangeTo] = useState(1);
  // 'report' = paginated paper pages, 'dashboard' = responsive grid.
  // Auto-detected from how the insight was authored; a header toggle lets the
  // reader switch either insight into the other layout on demand.
  const [layoutMode, setLayoutMode] = useState(null); // null until report loads

  // Resolve the applied theme's logo (report's own theme first, else the
  // tenant default theme) — shown in the header and used on PDF export.
  useEffect(() => {
    let alive = true;
    const rt = report?.config?.reportTheme;
    if (rt?.logo_url) { setThemeLogo(rt.logo_url); setThemeLogoPos(rt.logo_position || 'left'); return undefined; }
    api.getDefaultTheme().then(t => {
      if (!alive) return;
      if (t && t.logo_url) { setThemeLogo(t.logo_url); setThemeLogoPos(t.logo_position || 'left'); }
      else {
        // No theme logo → fall back to the tenant brand/template logo so exports
        // (PDF/print) still carry branding.
        const bl = getBranding()?.logo_url;
        if (bl) { setThemeLogo(bl); setThemeLogoPos('left'); }
      }
    }).catch(() => {
      if (!alive) return;
      const bl = getBranding()?.logo_url;
      if (bl) { setThemeLogo(bl); setThemeLogoPos('left'); }
    });
    return () => { alive = false; };
  }, [report]);

  // Detect mobile viewport
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Per-visual slicer state
  const [slicerValues, setSlicerValues] = useState({});
  const [slicerSelections, setSlicerSelections] = useState({});
  const [slicerSettings, setSlicerSettings] = useState({});
  const canvasRef = React.useRef(null);
  const [canvasWidth, setCanvasWidth] = useState(1200);
  const [canvasHeight, setCanvasHeight] = useState(800);

  // Parse drill-through filter from URL
  useEffect(() => {
    if (drillFilterParam) {
      try { setDrillThroughFilter(JSON.parse(decodeURIComponent(drillFilterParam))); } catch {}
    }
  }, [drillFilterParam]);

  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const ro = new ResizeObserver(entries => {
      for (const entry of entries) { setCanvasWidth(entry.contentRect.width - 48); setCanvasHeight(entry.contentRect.height); }
    });
    ro.observe(el);
    setCanvasWidth(el.clientWidth - 48);
    setCanvasHeight(el.clientHeight);
    return () => ro.disconnect();
  }, [report]);

  // Per-visual query results
  const [visualResults, setVisualResults] = useState({});
  // Cross-filter
  const [crossFilter, setCrossFilter] = useState(null);
  // Global filters
  const [globalFilterDims, setGlobalFilterDims] = useState([]);
  const [globalFilterValues, setGlobalFilterValues] = useState({});
  // Phase 2 — page-scope filters (keyed by page id).
  const [pageFilterDims, setPageFilterDims] = useState({});
  const [pageFilterValues, setPageFilterValues] = useState({});
  const coachLastFilterRef = useRef(null); // dim the Coach applied (for one-tap undo)

  // Phase 6: the autonomous Coach may APPLY a reversible report filter for the
  // user (only after explicit server-recorded confirmation) — a pure UI-state
  // change with one-tap undo. No report/data is mutated.
  useEffect(() => onCoachAction((action) => {
    if (action.type === 'apply_filter') {
      const dims = allFields.filter(f => f.semantic_type === 'dimension').map(f => f.name);
      const dim = dims.find(d => !globalFilterDims.includes(d));
      if (!dim) return false;
      setGlobalFilterDims(prev => (prev.includes(dim) ? prev : [...prev, dim]));
      coachLastFilterRef.current = dim;
      emitCoachEvent('filter_applied', 'reports-filter-panel');
      return true;
    }
    if (action.type === 'undo_filter') {
      const dim = coachLastFilterRef.current;
      if (dim) {
        setGlobalFilterDims(prev => prev.filter(d => d !== dim));
        setGlobalFilterValues(prev => { const c = { ...prev }; delete c[dim]; return c; });
        coachLastFilterRef.current = null;
      }
      return true;
    }
    return false;
  }), [allFields, globalFilterDims]);
  const [asOfOpen, setAsOfOpen] = useState(false);   // "As of" snapshot-date popover
  const [globalSlicerValues, setGlobalSlicerValues] = useState({});
  const [viewPageId, setViewPageId] = useState('page_1');
  const [viewAiContents, setViewAiContents] = useState({});
  const [loadingVisuals, setLoadingVisuals] = useState(new Set());
  const [resultsReady, setResultsReady] = useState(false);
  // Safety net so headless export can't hang on a stuck/errored visual: signal
  // print-ready after a grace period even if a result never resolves.
  const [printSettle, setPrintSettle] = useState(false);
  // Monotonic token so a stale/overlapping refresh can't overwrite fresh results
  // (root cause of visuals showing blank until several manual refreshes).
  const refreshSeqRef = useRef(0);
  const [loadingAiVisuals, setLoadingAiVisuals] = useState(new Set());
  // Slow-query UX: after 5s of waiting on visual queries, surface a spinner
  // with a Cancel button so the user isn't stuck staring at a frozen page.
  const [slowQuery, setSlowQuery] = useState(false);
  const slowTimerRef = useRef(null);
  useEffect(() => {
    if (loadingVisuals.size > 0 || loadingAiVisuals.size > 0) {
      if (!slowTimerRef.current) slowTimerRef.current = setTimeout(() => setSlowQuery(true), 5000);
    } else {
      if (slowTimerRef.current) { clearTimeout(slowTimerRef.current); slowTimerRef.current = null; }
      setSlowQuery(false);
    }
    return () => {};
  }, [loadingVisuals, loadingAiVisuals]);
  const cancelRunningQuery = () => {
    refreshSeqRef.current = (refreshSeqRef.current || 0) + 1; // supersede in-flight refresh
    setLoadingVisuals(new Set());
    setLoadingAiVisuals(new Set());
    setResultsReady(true); // stop per-visual "Loading data…" spinners after cancel
    setSlowQuery(false);
    if (slowTimerRef.current) { clearTimeout(slowTimerRef.current); slowTimerRef.current = null; }
  };

  const getVisuals = (pageId) => {
    if (!report) return [];
    const config = report.config || {};
    const qdef = report.query_definition || {};
    // When a pageId is supplied for a multi-page report, scope to THAT page's
    // visuals only. The query/refresh path must be page-scoped — querying every
    // page's visuals with the active page's filters cross-contaminates results
    // (a visual on another page gets re-run with the wrong page's filters and
    // its cached result is stomped). The render path stays unscoped elsewhere.
    const pageScoped = (pageId && config.pageVisuals && config.pageVisuals[pageId])
      ? config.pageVisuals[pageId].filter(Boolean)
      : null;
    const visuals = pageScoped
      || config.visuals
      || (config.pageVisuals ? Object.values(config.pageVisuals).flat().filter(Boolean) : null)
      || [{ id: 'v1', vizType: report.visualization_type || 'table', title: 'Visual 1' }];
    return visuals.map((v, i) => ({
      ...v,
      rowDims: v.rowDims || (i === 0 ? qdef.dimensions || [] : []),
      colDim: v.colDim !== undefined ? v.colDim : (i === 0 ? qdef.column_dimension || config.colDim || '' : ''),
      valueMeasures: v.valueMeasures || (i === 0 ? qdef.measures || [] : []),
      filterDims: v.filterDims || (i === 0 ? config.slicerDimensions || [] : []),
    }));
  };

  const executeVisualQuery = useCallback(async (vis, cf, gfValues) => {
    // SQL-snapshot visuals (saved from an AI Explore raw-SQL answer) carry their
    // own data and have no semantic dimensions — render the stored snapshot
    // directly rather than running a semantic query that would fail.
    if (vis.sqlMode && Array.isArray(vis.sqlData)) {
      return { data: vis.sqlData, pivotResult: null };
    }
    const rd = vis.rowDims || [];
    const vm = vis.valueMeasures || [];
    if (!rd.length && !vm.length) return null;
    const qdef = report?.query_definition || {};
    // Base report filters + drill-through filter from the URL are passed as drillFilters
    const baseFilters = [...(qdef.filters || [])];
    if (drillThroughFilter) {
      baseFilters.push({ field: drillThroughFilter.field, operator: 'equals', value: drillThroughFilter.value });
    }
    // Reshape per-visual slicer selections into the {<vizId>_<field>: value} format the engine expects
    const visSlicerSel = {};
    for (const dim of (vis.filterDims || [])) {
      const sel = slicerSelections[vis.id + '_' + dim];
      if (sel) visSlicerSel[vis.id + '_' + dim] = sel;
    }
    // Cross-filter scope: a slicer with an author-defined visual list only
    // filters those visuals; others ignore its selection.
    const slicerScopes = {};
    Object.keys(slicerSettings || {}).forEach(d => { const sc = slicerSettings[d]?.scope; if (Array.isArray(sc) && sc.length) slicerScopes[d] = sc; });
    const { result, pivotResult, triangleResult, comparisonResult } = await runVisualQuery({
      vis, allFields, modelId: qdef.model_id,
      slicerSelections: visSlicerSel,
      globalFilterValues: gfValues || {},
      crossFilter: cf,
      drillFilters: baseFilters,
      markColor: '', markDetail: '',
      visualConfigs: report?.config?.visualConfigs || {},
      safeEvalMath,
      asOfDate: (gfValues || {})['__asof__'] || undefined,
      slicerScopes,
    });
    if (!result && !triangleResult && !comparisonResult) return null;
    return { ...(result || {}), ...(comparisonResult || {}), pivotResult, triangleResult, comparisonResult };
  }, [report, allFields, slicerSelections, slicerSettings, drillThroughFilter]);

  // Assemble the export context (visuals + data + filters + governance) for the
  // unified Export dialog. Reads already-loaded query results, and falls back to
  // the rendered DOM table so report-layout / detail tables export what's shown.
  const buildExportContext = useCallback(() => {
    // Concatenate every rendered table under a visual container (report layout
    // paginates one detail table across many [data-visual-id] chunks).
    const extractDomConcat = (id) => {
      try {
        const conts = document.querySelectorAll(`[data-visual-id="${id}"]`);
        let columns = [];
        const rows = [];
        conts.forEach(cont => {
          cont.querySelectorAll('table').forEach(table => {
            const head = [...table.querySelectorAll('thead th, thead td')].map(c => (c.innerText || '').trim()).filter(Boolean);
            if (head.length && !columns.length) columns = head;
            const cols = columns.length ? columns : head;
            [...table.querySelectorAll('tbody tr')].forEach(tr => {
              const cells = [...tr.children].map(td => (td.innerText || '').trim());
              if (cells.every(c => !c)) return;
              const o = {}; cols.forEach((c, i) => { o[c] = cells[i] ?? ''; }); rows.push(o);
            });
          });
        });
        return columns.length && rows.length ? { columns, rows } : null;
      } catch { return null; }
    };
    const cfgVisuals = getVisuals().filter(v => !['rich_text', 'shape', 'image'].includes(v.vizType));
    // Prefer the visual ids actually rendered in the DOM (source of truth).
    const domIds = [...new Set([...document.querySelectorAll('[data-visual-id]')].map(e => e.getAttribute('data-visual-id')))];
    const ids = domIds.length ? domIds : cfgVisuals.map(v => v.id);
    // Flatten a pivot matrix into the standard {columns, rows, totals} shape so
    // the export renders the on-screen pivot layout (row dims × column values ×
    // measures + row totals) with an exact grand-total row.
    const flattenPivot = (id, pr) => {
      const { data = [], grand_total, pivot_columns = [], row_dimensions = [], measures = [] } = pr || {};
      if (!data.length) return null;
      const vcfg = { ...(report?.config?.visualConfigs?.[id] || {}), ...(viewVizOverrides[id] || {}) };
      const showGT = vcfg.grandTotals !== false;
      const fmt = fmtForVisual(id);
      const multi = measures.length > 1;
      const mLabel = (m) => resolveLabel(allFields, m) || m;
      const columns = [...row_dimensions];
      const column_labels = {};
      row_dimensions.forEach(rd => { column_labels[rd] = resolveLabel(allFields, rd) || rd; });
      pivot_columns.forEach(cv => measures.forEach(m => {
        const k = `${cv}__${m}`; columns.push(k);
        column_labels[k] = multi ? `${cv} — ${mLabel(m)}` : String(cv);
      }));
      if (showGT) measures.forEach(m => {
        const k = `__rowtotal__${m}`; columns.push(k);
        column_labels[k] = multi ? `Total — ${mLabel(m)}` : 'Total';
      });
      const rows = data.map(r => {
        const o = {};
        row_dimensions.forEach(rd => { o[rd] = r[rd]; });
        pivot_columns.forEach(cv => measures.forEach(m => { o[`${cv}__${m}`] = r[`${cv}__${m}`] ?? null; }));
        if (showGT) measures.forEach(m => { o[`__rowtotal__${m}`] = pivot_columns.reduce((s, cv) => s + (r[`${cv}__${m}`] || 0), 0); });
        return o;
      });
      let totals = null;
      if (showGT && grand_total) {
        const cells = {};
        columns.forEach((k, i) => {
          if (i === 0) { cells[k] = 'Grand Total'; return; }
          if (row_dimensions.includes(k)) { cells[k] = ''; return; }
          if (k.startsWith('__rowtotal__')) {
            const m = k.slice('__rowtotal__'.length);
            cells[k] = fmt(pivot_columns.reduce((s, cv) => s + (grand_total[`${cv}__${m}`] || 0), 0));
          } else {
            cells[k] = grand_total[k] !== undefined ? fmt(grand_total[k]) : '';
          }
        });
        totals = { label: 'Grand Total', cells };
      }
      return { columns, column_labels, rows, totals, subtotals: [] };
    };
    // Flatten a period-over-period comparison into Current/Prior/Variance/Var%
    // columns per measure with the exact on-screen Total row.
    const flattenComparison = (id, res) => {
      const { data = [], dimensions = [], measures = [], comparison_columns = {}, current_period, prior_period } = res || {};
      if (!data.length) return null;
      const fmt = fmtForVisual(id);
      const curL = String(current_period?.value ?? 'Current');
      const priL = String(prior_period?.value ?? 'Prior');
      const columns = [...dimensions];
      const column_labels = {};
      dimensions.forEach(d => { column_labels[d] = resolveLabel(allFields, d) || d; });
      const mk = {};
      measures.forEach(m => {
        const ml = resolveLabel(allFields, m) || m;
        const kc = `${m}__cur`, kp = `${m}__pri`, kv = `${m}__var`, kpct = `${m}__pct`;
        columns.push(kc, kp, kv, kpct);
        column_labels[kc] = `${ml} — ${curL}`; column_labels[kp] = `${ml} — ${priL}`;
        column_labels[kv] = `${ml} — Variance`; column_labels[kpct] = `${ml} — Var %`;
        mk[m] = { kc, kp, kv, kpct };
      });
      const rows = data.map(r => {
        const o = {};
        dimensions.forEach(d => { o[d] = r[d]; });
        measures.forEach(m => {
          const cols = comparison_columns[m] || {};
          const cur = r[cols.current] ?? 0, pri = r[cols.prior] ?? 0;
          const vv = r[cols.variance] ?? (cur - pri);
          const pct = r[cols.variance_pct];
          const kk = mk[m];
          o[kk.kc] = cur; o[kk.kp] = pri; o[kk.kv] = vv;
          o[kk.kpct] = (pct === null || pct === undefined) ? '' : `${pct > 0 ? '+' : ''}${pct.toFixed(1)}%`;
        });
        return o;
      });
      const cells = {};
      columns.forEach((k, i) => { cells[k] = i === 0 ? 'Total' : ''; });
      measures.forEach(m => {
        const cols = comparison_columns[m] || {};
        const tCur = data.reduce((s, r) => s + ((r[cols.current] ?? 0) || 0), 0);
        const tPri = data.reduce((s, r) => s + ((r[cols.prior] ?? 0) || 0), 0);
        const tVar = tCur - tPri;
        const tPct = tPri !== 0 ? (tVar / tPri * 100) : null;
        const kk = mk[m];
        cells[kk.kc] = fmt(tCur, m); cells[kk.kp] = fmt(tPri, m); cells[kk.kv] = fmt(tVar, m);
        cells[kk.kpct] = tPct === null ? '—' : `${tVar > 0 ? '+' : ''}${tPct.toFixed(1)}%`;
      });
      return { columns, column_labels, rows, totals: { label: 'Total', cells }, subtotals: [] };
    };

    const visuals = ids.map((id, idx) => {
      const cfg = cfgVisuals.find(v => v.id === id) || cfgVisuals[idx] || {};
      const res = visualResults[id] || (cfg.id ? visualResults[cfg.id] : null) || {};
      const vizType = cfg.vizType || 'table';
      const pageId = cfg._reportPageId || cfg.pageId || (report?.config?.pages?.[0]?.id);
      // Pivot / comparison: export the exact on-screen layout + totals.
      let prebuilt = null;
      if (vizType === 'pivot' && res.pivotResult) prebuilt = flattenPivot(id, res.pivotResult);
      else if ((vizType === 'comparison' || vizType === 'comparison_table') && res.comparison_columns) prebuilt = flattenComparison(id, res);
      if (prebuilt) {
        return {
          id, title: cfg.title || 'Visual', type: vizType,
          columns: prebuilt.columns, column_labels: prebuilt.column_labels,
          rows: prebuilt.rows, total_rows: prebuilt.rows.length,
          totals: prebuilt.totals, subtotals: prebuilt.subtotals || [],
          _prebuilt: true, selector: `[data-visual-id="${id}"]`, pageId,
        };
      }
      let rows = res.data || res.result?.data || (cfg.sqlMode && Array.isArray(cfg.sqlData) ? cfg.sqlData : []) || [];
      let columns = rows.length ? Object.keys(rows[0]) : [];
      if (!rows.length) {
        const dom = extractDomConcat(id);
        if (dom) { rows = dom.rows; columns = dom.columns; }
      }
      const fo = cfg.fieldOverrides || report?.config?.fieldOverrides || report?.config?.visualConfigs?.[id]?.fieldOverrides || {};
      const column_labels = {};
      columns.forEach(c => { column_labels[c] = resolveLabel(allFields, c, fo); });
      return {
        id, title: cfg.title || 'Visual', type: vizType,
        columns, column_labels, rows, total_rows: rows.length,
        selector: `[data-visual-id="${id}"]`, pageId,
      };
    }).filter(v => v.rows.length || v.columns.length);
    // Compose EXACT grand-total + per-group subtotal rows (mirroring TableRenderer)
    // so the PPTX/Excel export matches the on-screen table verbatim — including
    // avg/count group aggregations, not just sums.
    const TABLE_TYPES_EXP = ['table', 'pivot', 'pivot_table', 'comparison', 'comparison_table'];
    const enrichTotals = (v) => {
      if (v._prebuilt) return v;
      if (!TABLE_TYPES_EXP.includes(v.type) || !v.rows.length) return v;
      const vcfg = { ...(report?.config?.visualConfigs?.[v.id] || {}), ...(viewVizOverrides[v.id] || {}) };
      const cfgV = cfgVisuals.find(c => c.id === v.id) || {};
      const rowDims = cfgV.rowDims || vcfg.rowDims || [];
      const cols = v.columns;
      const fmt = fmtForVisual(v.id);
      const isNum = (k) => {
        if (rowDims.includes(k)) return false;
        const f = allFields.find(ff => ff.name === k);
        if (f && f.semantic_type === 'measure') return true;
        if (f && f.semantic_type === 'dimension') return false;
        return v.rows.filter(r => typeof r[k] === 'number').length > v.rows.length * 0.5;
      };
      const numericCols = cols.filter(isNum);
      // Sort rows by dimensions so group order + subtotal positions align with screen.
      let rows = v.rows;
      if (rowDims.length) {
        rows = [...v.rows].sort((a, b) => {
          for (const d of rowDims) {
            const av = a[d], bv = b[d];
            if (av === bv) continue;
            if (av == null) return 1; if (bv == null) return -1;
            if (typeof av === 'number' && typeof bv === 'number') return av - bv;
            return String(av).localeCompare(String(bv));
          }
          return 0;
        });
      }
      let totals = null;
      if (vcfg.grandTotals && numericCols.length) {
        const label = vcfg.grandTotalLabel ?? 'Grand Total';
        const sums = {}; numericCols.forEach(k => { sums[k] = rows.reduce((a, r) => a + (typeof r[k] === 'number' ? r[k] : 0), 0); });
        const cells = {}; cols.forEach((k, i) => { cells[k] = i === 0 ? label : (numericCols.includes(k) ? fmt(sums[k], k) : ''); });
        totals = { label, cells };
      }
      const groupByDims = vcfg.groupBy || (vcfg.subtotals && rowDims.length >= 2 ? [rowDims[0]] : []);
      const subtotals = [];
      if (groupByDims.length && vcfg.groupFooter !== false) {
        const agg = vcfg.groupAggregation || 'sum';
        const tmpl = vcfg.subtotalLabel ?? 'Subtotal';
        let gkPrev = null, sums = {}, count = 0;
        const flush = (endIdx, gk) => {
          const labels = gk.split('|||');
          const label = /\{group\}/.test(tmpl) ? tmpl.replace(/\{group\}/g, labels.join(' / ')) : tmpl;
          const cells = {};
          cols.forEach((k, i) => {
            if (i === 0) { cells[k] = label; return; }
            if (!numericCols.includes(k)) { cells[k] = ''; return; }
            const val = agg === 'avg' ? (count > 0 ? sums[k] / count : 0) : agg === 'count' ? count : (sums[k] || 0);
            cells[k] = fmt(val, k);
          });
          subtotals.push({ afterRowIndex: endIdx, label, cells });
        };
        rows.forEach((r, i) => {
          const gk = groupByDims.map(d => String(r[d] ?? '')).join('|||');
          if (gkPrev !== null && gk !== gkPrev) { flush(i - 1, gkPrev); sums = {}; count = 0; }
          gkPrev = gk; count++;
          numericCols.forEach(k => { sums[k] = (sums[k] || 0) + (typeof r[k] === 'number' ? r[k] : 0); });
        });
        if (gkPrev !== null) flush(rows.length - 1, gkPrev);
      }
      return { ...v, rows, totals, subtotals };
    };
    const enrichedVisuals = visuals.map(enrichTotals);
    const filters = [];
    Object.keys(globalFilterValues || {}).forEach(k => {
      if (k === '__asof__') return;
      const val = globalFilterValues[k];
      if (val) filters.push({ label: k.replace(/_/g, ' '), value: String(val) });
    });
    return {
      title: report?.name || 'Report',
      defaultFilename: `${report?.name || 'report'}`,
      filters,
      asOf: globalFilterValues?.['__asof__'] || null,
      pages: report?.config?.pages || [],
      currentPageId: viewPageId,
      currentVisualId: visuals[0]?.id,
      selectedVisualIds: visuals.map(v => v.id),
      visuals: enrichedVisuals,
      governance: { certified: [], lineage: [], disclosure: '' },
      pageSelector: '[data-visual-id]',
      // The logo users see at the top of this report (its theme logo), so the
      // export carries the SAME branding rather than only the tenant default.
      themeLogoUrl: themeLogo || null,
      // The report's theme colour so exported tables match the on-screen brand.
      themeColor: (report?.config?.reportTheme?.primary)
        || (Array.isArray(report?.config?.reportTheme?.colors) && report.config.reportTheme.colors[0])
        || null,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report, visualResults, globalFilterValues, viewPageId, allFields, themeLogo, viewVizOverrides]);

  const refreshAllVisuals = useCallback(async (cf, gfValues) => {
    if (!report) return;
    const myToken = ++refreshSeqRef.current;
    // Page-scope the query path: only query the ACTIVE page's visuals, with the
    // active page's filters. Querying other pages' visuals here would apply the
    // wrong page's filters and stomp their cached results in the shared store.
    const visuals = getVisuals(viewPageId);
    // Merge the active page's page-scope filters over dashboard-scope filters.
    const effValues = { ...(gfValues || {}), ...(pageFilterValues[viewPageId] || {}) };
    const vizIds = visuals.filter(v => v.vizType !== 'ai_text').map(v => v.id);
    setLoadingVisuals(new Set(vizIds));
    const results = {};
    // Run all visual queries in parallel — sequential await made a dashboard
    // take the SUM of every visual's latency (17s for two slow visuals).
    await Promise.all(visuals.map(async (v) => {
      try {
        results[v.id] = await executeVisualQuery(v, cf, effValues);
      } catch {
        // Transient hiccup (e.g. a slow/overloaded backend when every visual
        // queries at once) — retry once before giving up, so a random visual
        // doesn't intermittently render blank on open.
        try {
          await new Promise(r => setTimeout(r, 400));
          results[v.id] = await executeVisualQuery(v, cf, effValues);
        } catch { results[v.id] = null; }
      }
      if (refreshSeqRef.current === myToken) {
        setLoadingVisuals(prev => { const next = new Set(prev); next.delete(v.id); return next; });
      }
    }));
    // A newer refresh superseded this one — drop these (possibly stale) results.
    if (refreshSeqRef.current !== myToken) return;
    // Merge (not replace): preserve other pages' already-loaded results so
    // switching pages never blanks a page we're not currently querying.
    setVisualResults(prev => ({ ...prev, ...results }));
    setResultsReady(true);
  }, [report, executeVisualQuery, pageFilterValues, viewPageId]);

  // Initial load
  useEffect(() => {
    if (!reportId) { navigate('/reports'); return; }
    // Wait for permissions so we request the right stage: authors/admins see the
    // live (latest) content so "Report view" matches the builder, while business
    // viewers see the published production snapshot.
    if (!permsLoaded) return;
    (async () => {
      try {
        let rpt;
        const canEdit = hasPermission('reports:write');
        try {
          rpt = await api.getReport(reportId, canEdit ? undefined : 'production');
        } catch (e) {
          // Pure dashboards live in a separate collection — fall back so the
          // shared viewer (and its themed header) works on /dashboards/view too.
          rpt = await api.getDashboard(reportId);
        }
        setReport(rpt);
        // Phase 2 — surface the author-placed filters (dashboard + page scope)
        // so viewers land on a report that already shows its filter bars.
        const cfg0 = rpt.config || {};
        if (Array.isArray(cfg0.globalFilterDims)) setGlobalFilterDims(cfg0.globalFilterDims);
        if (cfg0.globalFilterValues && typeof cfg0.globalFilterValues === 'object') setGlobalFilterValues(cfg0.globalFilterValues);
        // Seed per-visual slicer picks (e.g. an Explore "State = CA" filter saved
        // as an editable tile slicer) so the viewer lands on the same filtered data.
        if (cfg0.slicerSelections && typeof cfg0.slicerSelections === 'object') setSlicerSelections(cfg0.slicerSelections);
        if (cfg0.slicerSettings && typeof cfg0.slicerSettings === 'object') setSlicerSettings(cfg0.slicerSettings);
        if (cfg0.pageFilterDims && typeof cfg0.pageFilterDims === 'object') setPageFilterDims(cfg0.pageFilterDims);
        if (cfg0.pageFilterValues && typeof cfg0.pageFilterValues === 'object') setPageFilterValues(cfg0.pageFilterValues);
        // Record a server-side view timestamp (powers the stale-content AI Insight).
        api.markReportViewed(reportId).catch(() => {});
        // Remember this insight so it can appear in the Drill-Through
        // "Recently viewed" section on the builder.
        try {
          const { recordInsightView } = await import('@/lib/recentInsights');
          recordInsightView({ id: reportId, name: rpt.name || rpt.title, kind: 'Report' });
        } catch (_) { /* ignore */ }
        const qdef = rpt.query_definition || {};
        if (qdef.model_id) {
          const model = await api.getSemanticModelFull(qdef.model_id);
          setFiscalStartMonth(Number(model.fiscal_year_start_month) || 1);
          const fields = [];
          const seen = new Set();
          (model.entities || []).forEach(e => (e.attributes || []).forEach(a => {
            // Dedupe PER-ENTITY (not globally): the same field name can exist on
            // several entities (e.g. FULL_LEGAL_NAME on Insured, Claimant and
            // Adjuster) and each must be kept so it can carry its own label.
            const key = (e.entity_id || e.name || '') + '::' + a.name;
            if (!seen.has(key)) { fields.push({ ...a, entity_id: e.entity_id, entity_name: e.name, entity_logical_name: e.logical_name || '' }); seen.add(key); }
          }));
          // Register model-level calculated measures (computed client-side via formula)
          (model.calculated_measures || []).forEach(cm => {
            if (!seen.has(cm.name)) {
              fields.push({ name: cm.name, display_name: cm.name, description: cm.name, help: cm.description || '', semantic_type: 'measure', data_type: 'float', aggregation: cm.aggregation || 'sum', formula: cm.formula, is_calculated: true, _isModelMeasure: true });
              seen.add(cm.name);
            }
          });
          // Re-register virtual fields from saved config (e.g., "Sale Date__1")
          const savedVisuals = rpt.config?.visuals || [];
          savedVisuals.forEach(v => {
            (v.rowDims || []).forEach(dim => {
              if (dim.includes('__') && !seen.has(dim)) {
                const sourceField = dim.split('__')[0];
                const sourceMeta = fields.find(f => f.name === sourceField);
                if (sourceMeta) { fields.push({ ...sourceMeta, name: dim, _sourceField: sourceField }); seen.add(dim); }
              }
            });
          });
          // Mirror the builder's entity-qualified identity so duplicate-named
          // fields resolve to a friendly, entity-scoped label ("Insured · Full
          // Legal Name") instead of a raw "<entity_id>::FIELD" key in headers.
          const qualifyDup = (pred) => {
            const counts = {};
            fields.forEach(f => { if (pred(f)) counts[f.name] = (counts[f.name] || 0) + 1; });
            fields.forEach(f => {
              if (pred(f) && counts[f.name] > 1 && !String(f.name).includes('::')) {
                const base = f.name;
                const baseLabel = f.business_name || f.display_name || f.label || base.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                f._baseName = base;
                f.name = f.entity_id + '::' + base;
                f.business_name = entityQualifiedLabel(f.entity_name, f.entity_logical_name, baseLabel);
              }
            });
          };
          qualifyDup(f => f && f.semantic_type !== 'measure' && !f.is_calculated && f.entity_id && !f._sourceField);
          qualifyDup(f => f && f.semantic_type === 'measure' && !f._isModelMeasure && !f.is_calculated && f.entity_id && !f._sourceField);
          setAllFields(fields);
        }
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
      try { const d = await api.getReportDiscussions(reportId); setDiscussions(d); } catch {}
      try { setViewAlerts(await api.getAlerts()); } catch {}
      try { setViewSchedules(await api.getSchedules()); } catch {}
    })();
  }, [reportId, navigate, permsLoaded]);

  // Auto-detect the initial layout from how the insight was authored.
  useEffect(() => {
    if (report && layoutMode === null) {
      // Print mode normally forces the paginated report layout, EXCEPT pixel-perfect
      // dashboard exports, which render the actual dashboard grid.
      const forceReport = printMode && !pixelMode;
      setLayoutMode(forceReport ? 'report' : (report.config?.designerMode === 'report' ? 'report' : 'dashboard'));
    }
  }, [report, layoutMode, printMode, pixelMode]);

  // Pages — on load, land on the ?page=<id> deep link when it points at a
  // visible page, otherwise the FIRST visible page. Runs only on report load
  // ([report] dep), so a viewer's later page switches are preserved.
  useEffect(() => {
    if (!report) return;
    const pgs = report.config?.pages || [];
    if (pgs.length <= 1) return;
    const visible = pgs.filter(isPageVisible);
    if (!visible.length) return;
    const deep = new URLSearchParams(window.location.search).get('page');
    setViewPageId((deep && visible.some(p => p.id === deep)) ? deep : visible[0].id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report]);

  // Headless print mode: strip app chrome so server-side Chromium captures only
  // the report pages (charts/maps render natively — always appear in the PDF).
  useEffect(() => {
    if (printMode) {
      document.body.classList.add('report-printing');
      const t = setTimeout(() => setPrintSettle(true), 8000);
      return () => { document.body.classList.remove('report-printing'); clearTimeout(t); };
    }
  }, [printMode]);

  // Execute all visuals after report loads or filters change
  useEffect(() => {
    if (!report) return;
    // For semantic (model-based) reports, wait until the model's fields are
    // loaded — running before then yields empty results that used to blank out
    // freshly-added visuals until a manual refresh.
    if (report.query_definition?.model_id && allFields.length === 0) return;
    refreshAllVisuals(crossFilter, globalFilterValues);
  }, [report, allFields, crossFilter, globalFilterValues, slicerSelections, refreshAllVisuals]);

  // Keyboard shortcut: press "R" to rerun every visual on the open report.
  useGlobalShortcut({
    id: 'report-view.refresh-data',
    keys: 'r',
    label: 'Refresh data (rerun all queries)',
    category: 'Data',
    handler: () => refreshAllVisuals(crossFilter, globalFilterValues),
  });


  // Rerun AI text blocks in view mode when filters change
  useEffect(() => {
    if (!report) return;
    const allVisuals = getVisuals();
    const aiVisuals = allVisuals.filter(v => v.vizType === 'ai_text' && (v.aiRerunOnFilter !== false || v.aiRerunOnClick));
    if (!aiVisuals.length) return;
    const timer = setTimeout(async () => {
      const otherVisuals = allVisuals.filter(v => v.vizType !== 'ai_text');
      let sampleData = [];
      otherVisuals.forEach(v => { const vr = visualResults[v.id]; if (vr?.data) sampleData = sampleData.concat(vr.data.slice(0, 10)); });
      if (!sampleData.length) return;
      const filterParts = [];
      if (crossFilter) filterParts.push(`Cross-filtered by ${crossFilter.field.replace(/_/g, ' ')} = "${crossFilter.value}"`);
      Object.entries(globalFilterValues).forEach(([k, v]) => {
        if (!v) return;
        if (k === '__daterange__' && v.preset) filterParts.push(`Date range: ${String(v.preset).replace(/_/g, ' ')}`);
        else if (typeof v !== 'object') filterParts.push(`Global filter: ${k.replace(/_/g, ' ')} = "${v}"`);
      });
      const filterContext = filterParts.length > 0 ? `\n\nIMPORTANT: The data is currently filtered. Active filters: ${filterParts.join('; ')}. Acknowledge these filters in your analysis.` : '';
      for (const v of aiVisuals) {
        try {
          setLoadingAiVisuals(prev => new Set([...prev, v.id]));
          const fullPrompt = (v.aiPrompt || report.name) + filterContext;
          const n = await api.generateNarrative(sampleData.slice(0, 20), [], [], fullPrompt);
          setViewAiContents(prev => ({ ...prev, [v.id]: (n.narrative || '') + (n.highlights ? '\n\n' + n.highlights.map(h => '\u2022 ' + h).join('\n') : '') }));
        } catch {}
        finally { setLoadingAiVisuals(prev => { const next = new Set(prev); next.delete(v.id); return next; }); }
      }
    }, 2000);
    return () => clearTimeout(timer);
  }, [crossFilter, globalFilterValues]);

  // Cascading slicers (viewer): narrow each slicer's values by the OTHER
  // slicers' current picks (e.g. pick a State → Agent slicer shows only that
  // state's agents). Recomputes live as selections change.
  const buildViewCascade = useCallback((excludeField) => {
    if (report?.config?.cascadeSlicers === false) return [];
    const selByField = {};
    getVisuals().forEach(v => (v.filterDims || []).forEach(dim => {
      const val = slicerSelections[v.id + '_' + dim];
      if (val) selByField[dim] = val;
    }));
    Object.entries(globalFilterValues || {}).forEach(([f, val]) => {
      if (val && !f.startsWith('__')) selByField[f] = val;
    });
    const out = [];
    Object.entries(selByField).forEach(([f, val]) => {
      if (f === excludeField) return;
      out.push(...filtersForSelection(f, val));
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slicerSelections, globalFilterValues, report]);

  const viewSelSig = useMemo(() => JSON.stringify(slicerSelections) + JSON.stringify(globalFilterValues), [slicerSelections, globalFilterValues]);

  // Load slicer values for per-visual filters (cascade-aware)
  useEffect(() => {
    if (!report) return;
    const modelId = report.query_definition?.model_id;
    if (!modelId) return;
    const dims = [...new Set(getVisuals().flatMap(v => v.filterDims || []))]
      .filter(dim => !isDateField(allFields.find(f => f.name === dim)));
    let cancelled = false;
    const t = setTimeout(async () => {
      for (const dim of dims) {
        try {
          const st = getSlicerSetting(slicerSettings, dim);
          const filters = st.cascade === false ? [] : buildViewCascade(dim);
          const sv = await api.executeQuery({ model_id: modelId, dimensions: [dim], measures: [], filters, limit: 500 });
          if (cancelled) return;
          const uniqueVals = [...new Set((sv.data || []).map(r => r[dim]).filter(Boolean))].sort();
          setSlicerValues(prev => ({ ...prev, [dim]: uniqueVals }));
        } catch {}
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report, viewSelSig]);

  // Load global filter slicer values (cascade-aware)
  useEffect(() => {
    if (!report) return;
    const modelId = report.query_definition?.model_id;
    if (!modelId) return;
    const pgDims = pageFilterDims[viewPageId] || [];
    const dims = [...new Set([...globalFilterDims, ...pgDims])].filter(dim => !isDateField(allFields.find(f => f.name === dim)));
    let cancelled = false;
    const t = setTimeout(async () => {
      for (const dim of dims) {
        try {
          const st = getSlicerSetting(slicerSettings, dim);
          const filters = st.cascade === false ? [] : buildViewCascade(dim);
          const sv = await api.executeQuery({ model_id: modelId, dimensions: [dim], measures: [], filters, limit: 500 });
          if (cancelled) return;
          const uniqueVals = [...new Set((sv.data || []).map(r => r[dim]).filter(Boolean))].sort();
          setGlobalSlicerValues(prev => ({ ...prev, [dim]: uniqueVals }));
        } catch {}
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report, globalFilterDims, pageFilterDims, viewPageId, viewSelSig]);

  const handleCrossFilter = (val, vizId, vis) => {
    if (!vis?.rowDims?.length) return;
    const field = vis.rowDims[0];
    if (crossFilter && crossFilter.field === field && crossFilter.value === val) {
      setCrossFilter(null);
    } else {
      setCrossFilter({ field, value: val, sourceVisualId: vizId });
    }
  };

  // Lazy thumbnail generation: capture when report loads with data and has no thumbnail
  const thumbnailCaptured = React.useRef(false);
  useEffect(() => {
    if (!report || !reportId || thumbnailCaptured.current) return;
    if (report.thumbnail && !refreshThumb) { thumbnailCaptured.current = true; return; }
    // Wait for at least one visual to have results
    const hasResults = Object.keys(visualResults).length > 0;
    if (!hasResults) return;

    // Delay to let charts render
    const timer = setTimeout(async () => {
      try {
        const el = canvasRef.current;
        if (!el) return;
        const { default: html2canvas } = await import('html2canvas');
        const canvas = await html2canvas(el, {
          backgroundColor: (document.documentElement.classList.contains('dark') ? '#09090b' : '#ffffff'), scale: 1, logging: false, useCORS: true,
          width: Math.min(el.scrollWidth, 1600), height: Math.min(el.scrollHeight, 900),
        });
        // Downscale to crisp 800px wide thumbnail
        const w = 800, h = Math.round((Math.min(el.scrollHeight, 900) / Math.min(el.scrollWidth, 1600)) * 800);
        const off = document.createElement('canvas');
        off.width = w; off.height = h;
        const ctx = off.getContext('2d');
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(canvas, 0, 0, w, h);
        const thumbUrl = off.toDataURL('image/jpeg', 0.85);
        if (thumbUrl.length > 500 && thumbUrl.length < 400000) {
          await api.saveReportThumbnail(reportId, thumbUrl);
          thumbnailCaptured.current = true;
        }
      } catch (err) { console.warn('Auto-thumbnail failed:', err); }
    }, 2000);
    return () => clearTimeout(timer);
  }, [report, reportId, visualResults]);

  // Context menu for discussions
  useEffect(() => {
    const page = document.querySelector('[data-testid="report-view-page"]');
    if (!page) return;
    const blockMenu = (e) => {
      e.preventDefault();
      e.stopImmediatePropagation();
      let el = e.target;
      while (el && el !== document.body) {
        if (el.getAttribute && el.getAttribute('data-visual-id')) {
          setContextMenu({ x: e.clientX, y: e.clientY, visualId: el.getAttribute('data-visual-id') });
          return false;
        }
        el = el.parentNode;
      }
      return false;
    };
    page.oncontextmenu = blockMenu;
    const winHandler = (e) => { if (page.contains(e.target)) e.preventDefault(); };
    window.addEventListener('contextmenu', winHandler, true);
    return () => { page.oncontextmenu = null; window.removeEventListener('contextmenu', winHandler, true); };
  }, [loading]);

  const captureReportState = () => ({
    globalFilterDims,
    globalFilterValues,
    slicerSelections,
    crossFilter,
  });

  const restoreReportState = (state) => {
    if (!state) return;
    if (state.globalFilterDims) setGlobalFilterDims(state.globalFilterDims);
    if (state.globalFilterValues) setGlobalFilterValues(state.globalFilterValues);
    if (state.pageFilterDims) setPageFilterDims(state.pageFilterDims);
    if (state.pageFilterValues) setPageFilterValues(state.pageFilterValues);
    if (state.slicerSelections) setSlicerSelections(state.slicerSelections);
    if (state.crossFilter) setCrossFilter(state.crossFilter);
  };

  // ── Sticky "as of": remember each viewer's last chosen snapshot date per
  // dashboard, so returning keeps their snapshot. Skipped for shared views
  // (?viewState=…) which carry their own state. A hydration guard stops the
  // persist effect from clearing storage on first mount (before we've read it).
  const asOfStorageKey = reportId ? `inzara.asof.${reportId}` : null;
  const asOfHydratedRef = useRef(false);
  useEffect(() => {
    if (!report || !asOfStorageKey) return;
    if (!viewStateParam) {
      try {
        const saved = localStorage.getItem(asOfStorageKey);
        if (saved) setGlobalFilterValues(prev => (prev['__asof__'] ? prev : { ...prev, __asof__: saved }));
      } catch { /* storage disabled */ }
    }
    asOfHydratedRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report, asOfStorageKey]);
  useEffect(() => {
    if (!asOfStorageKey || !asOfHydratedRef.current) return;
    try {
      const v = globalFilterValues['__asof__'];
      if (v) localStorage.setItem(asOfStorageKey, v); else localStorage.removeItem(asOfStorageKey);
    } catch { /* storage disabled */ }
  }, [globalFilterValues, asOfStorageKey]);

  // Apply author-configured default selections when the dashboard opens.
  // Runs once per dimension (guarded by ref) as soon as its values are available.
  const slicerDefaultsRef = useRef({});
  const slicerValuesSig = JSON.stringify({
    g: Object.keys(globalSlicerValues).map(k => k + ':' + (globalSlicerValues[k] || []).length),
    s: Object.keys(slicerValues).map(k => k + ':' + (slicerValues[k] || []).length),
  });
  useEffect(() => {
    if (!report) return;
    const mId = report.query_definition?.model_id;
    const settings = slicerSettings || {};
    Object.keys(settings).forEach(dim => {
      if (slicerDefaultsRef.current[dim]) return;
      const setting = getSlicerSetting(settings, dim);
      const ds = setting.defaultSelection || { type: 'all' };
      if (ds.type === 'all') { slicerDefaultsRef.current[dim] = true; return; }
      const pageHas = Object.keys(pageFilterDims || {}).some(pid => (pageFilterDims[pid] || []).includes(dim));
      const isGlobalDim = (globalFilterDims || []).includes(dim) || pageHas;
      const vals = globalSlicerValues[dim] || slicerValues[dim] || [];
      const isDate = isDateField(allFields.find(f => f.name === dim));
      const needVals = !['relative', 'remember'].includes(ds.type);
      if (needVals && !vals.length) return; // wait for values to load
      const empty = (r) => r == null || (Array.isArray(r) && !r.length);
      const applyGlobal = (r) => setGlobalFilterValues(p => { const n = { ...p }; if (!empty(r)) n[dim] = r; else delete n[dim]; return n; });
      const applyLocal = (r) => setSlicerSelections(p => { const n = { ...p }; getVisuals().forEach(v => { if ((v.filterDims || []).includes(dim)) n[v.id + '_' + dim] = r; }); return n; });
      const apply = (r) => { slicerDefaultsRef.current[dim] = true; if (isGlobalDim) applyGlobal(r); else applyLocal(r); };
      if (ds.type === 'top_measure' && ds.measure && mId) {
        api.executeQuery({ model_id: mId, dimensions: [dim], measures: [ds.measure], filters: [], limit: 500 })
          .then(r => { const rows = (r.data || []).filter(x => x[dim] != null); rows.sort((a, b) => (Number(b[ds.measure]) || 0) - (Number(a[ds.measure]) || 0)); apply(resolveDefaultSelection({ setting, values: vals, isDate, reportId, dim, topValue: rows.length ? rows[0][dim] : null })); })
          .catch(() => {});
      } else {
        apply(resolveDefaultSelection({ setting, values: vals, isDate, reportId, dim, userAttrs: user }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report, slicerSettings, slicerValuesSig, user]);


  // ── Phase 2: presence, share-view & follow mode (all context-only; every
  // viewer/follower re-fetches under their OWN permissions — D6). ──
  const [following, setFollowing] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('followLeader') || 'null'); } catch { return null; }
  });
  const [leaderCursor, setLeaderCursor] = useState(null);   // followed leader's live cursor
  const pageRef = useRef(null);
  const lastCursorTs = useRef(0);

  // Opening a shared view auto-starts a live spectate session (follow the sharer).
  useEffect(() => {
    if (!report || !followParam || followParam === me) return;
    const leader = { uid: followParam, name: followNameParam || 'A teammate' };
    setFollowing(leader);
    try { sessionStorage.setItem('followLeader', JSON.stringify(leader)); } catch { /* */ }
    startFollow(followParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report, followParam]);

  // Restore a shared view carried in the URL (?viewState=…).
  useEffect(() => {
    if (!report || !viewStateParam) return;
    try { restoreReportState(JSON.parse(decodeURIComponent(viewStateParam))); } catch { /* ignore */ }
     
  }, [report, viewStateParam]);

  // Announce what I'm viewing (drives "N viewing now") + re-establish any active follow.
  useEffect(() => {
    if (!report) return;
    connectMessaging();
    setViewing({ object_type: assetType, object_id: reportId, route: location.pathname + location.search, label: report.name });
    if (following?.uid) startFollow(following.uid);
    return () => setViewing(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report, reportId, assetType]);

  // As a leader, push my current route + filter state to my followers (context only).
  // Debounced so rapid filter changes don't spam the socket.
  useEffect(() => {
    if (!report) return;
    const t = setTimeout(() => {
      broadcastNav({ object_type: assetType, object_id: reportId, route: location.pathname + location.search, state: captureReportState(), label: report.name });
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalFilterValues, slicerSelections, crossFilter, viewPageId, report]);

  // As a follower, apply the leader's navigation/filter changes + live cursor.
  useEffect(() => {
    const off = onMessagingEvent((evt) => {
      if (evt.type === 'follow_cursor') {
        setFollowing((f) => {
          if (f && evt.leader_id === f.uid) setLeaderCursor({ x: evt.x, y: evt.y, name: evt.name });
          return f;
        });
        return;
      }
      if (evt.type !== 'nav') return;
      setFollowing((f) => {
        if (!f || evt.leader_id !== f.uid) return f;
        const ctx = evt.context || {};
        if (ctx.object_id && String(ctx.object_id) !== String(reportId)) {
          if (ctx.route) navigate(ctx.route);
        } else if (ctx.state) {
          restoreReportState(ctx.state);
        }
        return f;
      });
    });
    return () => off();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  // Broadcast my own cursor to followers (per-mille of the page surface).
  const onPageMouseMove = useCallback((e) => {
    const now = Date.now();
    if (now - lastCursorTs.current < 60) return;
    lastCursorTs.current = now;
    const rect = pageRef.current?.getBoundingClientRect();
    if (!rect || !rect.width) return;
    broadcastFollowCursor(Math.round(((e.clientX - rect.left) / rect.width) * 1000),
      Math.round(((e.clientY - rect.top) / rect.height) * 1000));
  }, []);

  const onFollowViewer = (v) => { setFollowing(v); try { sessionStorage.setItem('followLeader', JSON.stringify(v)); } catch { /* */ } };
  const onStopFollowing = () => {
    if (following?.uid) stopFollow(following.uid);
    setFollowing(null);
    setLeaderCursor(null);
    try { sessionStorage.removeItem('followLeader'); } catch { /* */ }
  };

  const [shareViewOpen, setShareViewOpen] = useState(false);
  const shareMyView = () => setShareViewOpen(true);
  const doShareView = async (target) => {
    setShareViewOpen(false);
    try {
      const encoded = encodeURIComponent(JSON.stringify(captureReportState()));
      const route = `${location.pathname}?id=${reportId}&viewState=${encoded}&follow=${me}&fn=${encodeURIComponent(user?.name || 'A teammate')}`;
      const attachment = { type: 'shared_view', route, label: report?.name || 'this view', object_type: assetType, object_id: reportId, from_id: me };
      if (target?.user_id) {
        // deliver to a specific teammate via a direct message
        const conv = await api.createConversation({ type: 'dm', participant_ids: [target.user_id] });
        await api.sendMessage(conv.conversation_id, { text: `${user?.name || 'Someone'} shared a view with you`, attachment });
        toast.success(`View shared with ${target.name}`, { action: { label: 'Open chat', onClick: () => navigate(`/threads?conversation=${conv.conversation_id}`) } });
      } else {
        // broadcast into the report's own chat
        const conv = await api.getObjectConversation(assetType, reportId, report?.name);
        await api.sendMessage(conv.conversation_id, { text: `${user?.name || 'Someone'} shared their current view`, attachment });
        toast.success("View shared to this report's chat");
        setRightPanelOpen(true);
        setRightPanelTab('chat');
      }
    } catch (e) { toast.error(e.message); }
  };

  // If discussion param is set, load and restore state + open thread
  useEffect(() => {
    if (!discussionParam || !report) return;
    (async () => {
      try {
        const disc = await api.getDiscussion(discussionParam);
        if (disc.report_state) restoreReportState(disc.report_state);
        setThreadId(discussionParam);
        setThreadOpen(true);
      } catch {}
    })();
  }, [discussionParam, report]);

  const startDiscussion = async (visualId) => {
    setDiscussionVisualId(visualId);
    setContextMenu(null);
    const el = document.querySelector(`[data-visual-id="${visualId}"]`);
    let screenshot = '';
    if (el) {
      try { const { default: html2canvas } = await import('html2canvas'); const canvas = await html2canvas(el, { backgroundColor: (document.documentElement.classList.contains('dark') ? '#09090b' : '#ffffff'), scale: 1.5, logging: false }); screenshot = canvas.toDataURL('image/png'); } catch {}
    }
    const vr = visualResults[visualId];
    const visuals = getVisuals();
    const vis = visuals.find(v => v.id === visualId);
    const dataCtx = {};

    // Capture the specific data point from the last hovered tooltip
    const hoveredRow = lastHoveredData.current;
    if (hoveredRow) {
      const dims = vis?.rowDims || [];
      const measures = vis?.valueMeasures || [];
      dims.forEach(d => { if (hoveredRow[d] !== undefined) dataCtx[d] = hoveredRow[d]; });
      measures.forEach(m => { if (hoveredRow[m] !== undefined) dataCtx[m] = hoveredRow[m]; });
    }

    // Capture active filters
    if (Object.keys(globalFilterValues).some(k => globalFilterValues[k])) {
      const filterCtx = {};
      Object.entries(globalFilterValues).forEach(([k, v]) => { if (v) filterCtx[k] = v; });
      dataCtx._filters = filterCtx;
    }
    if (crossFilter) {
      dataCtx._cross_filter = `${crossFilter.field} = ${crossFilter.value}`;
    }

    // Row count
    if (vr?.row_count) dataCtx._row_count = vr.row_count;

    setDiscussionCtx(dataCtx);
    setDiscussionScreenshot(screenshot);
    setDiscussionOpen(true);
  };

  // Saved per-field overrides (renames + format/aggregation props). Persisted at
  // config.fieldOverrides so the published report mirrors the builder exactly.
  const savedFieldOverrides = React.useMemo(() => report?.config?.fieldOverrides || {}, [report]);
  const formatValue = React.useMemo(
    () => createFormatValue(allFields, savedFieldOverrides, fiscalStartMonth),
    [allFields, savedFieldOverrides, fiscalStartMonth]
  );
  // Per-visual formatter — layers each visual result's `_measure_units`
  // (certified ratio → percent) over the base formatter.
  const fmtForVisual = (vid) => withMeasureUnits(formatValue, visualResults[vid]?._measure_units || visualResults[vid]?.result?._measure_units);
  const transformDataForView = (data, vis, dateParts) => {
    if (!data?.length || !Object.keys(dateParts).length) return data;
    const rd = vis.rowDims || [];
    const vm = vis.valueMeasures || [];
    const cd = vis.colDim || '';
    const MONTHS = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const DAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];
    let result = [...data];
    for (const [field, part] of Object.entries(dateParts)) {
      if (!part) continue;
      result = result.map(row => {
        const val = row[field];
        if (val == null) return row;
        const d = new Date(val);
        if (isNaN(d.getTime())) return row;
        let extracted, sortVal;
        switch (part) {
          case 'year': extracted = d.getFullYear(); sortVal = extracted; break;
          case 'quarter': extracted = 'Q' + (Math.floor(d.getMonth() / 3) + 1); sortVal = Math.floor(d.getMonth() / 3) + 1; break;
          case 'month': extracted = MONTHS[d.getMonth()]; sortVal = d.getMonth() + 1; break;
          case 'month_number': extracted = d.getMonth() + 1; sortVal = extracted; break;
          case 'week': { const start = new Date(d.getFullYear(), 0, 1); const wk = Math.ceil(((d - start) / 86400000 + start.getDay() + 1) / 7); extracted = 'W' + wk; sortVal = wk; break; }
          case 'day': extracted = d.getDate(); sortVal = extracted; break;
          case 'day_of_week': extracted = DAYS[d.getDay()]; sortVal = d.getDay(); break;
          case 'hour': extracted = d.getHours() + ':00'; sortVal = d.getHours(); break;
          default: extracted = val; sortVal = val;
        }
        return { ...row, [field]: extracted, ['_sort_' + field]: sortVal };
      });
      // Re-aggregate. MUST group by the Color/Series dimension too, otherwise
      // every series for a given x-value collapses into the first row's series
      // (e.g. 9 lines of business appear as ~4) with mis-summed measures.
      const grouped = {};
      const groupDims = cd ? [...rd, cd] : rd;
      result.forEach(row => {
        const key = groupDims.map(dim => row[dim]).join('|||');
        if (!grouped[key]) grouped[key] = { ...row };
        else { vm.forEach(m => { grouped[key][m] = (grouped[key][m] || 0) + (row[m] || 0); }); }
      });
      result = Object.values(grouped);
    }
    return result;
  };

  // ─── Forecasting on a report visual (Insights) ───
  const [fcDialog, setFcDialog] = useState(null); // { title, forecast }
  const [fcAlgos, setFcAlgos] = useState([]);
  useEffect(() => { api.getForecastAlgorithms().then(r => setFcAlgos((r.algorithms || []).map(a => a.name))).catch(() => {}); }, []);

  const openForecast = async (v) => {
    const rows = visualResults[v.id]?.data || [];
    if (!rows.length) { toast.error('No data to forecast'); return; }
    // Resolve dim/measure/series against the ACTUAL data columns — for sqlMode
    // visuals the config labels (e.g. "Written Premium YTD") differ from the SQL
    // output column names (e.g. "WRITTEN_PREMIUM_YTD").
    const keys = Object.keys(rows[0]);
    const nk = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const isNumCol = (k) => rows.some(r => r[k] !== null && r[k] !== '' && !Number.isNaN(Number(r[k])));
    const resolveKey = (want) => {
      if (!want) return null;
      if (keys.includes(want)) return want;
      const w = nk(want);
      return keys.find(k => nk(k) === w) || null;
    };
    let meas = resolveKey((v.valueMeasures || [])[0]) || keys.find(k => isNumCol(k) && k !== (v.rowDims || [])[0]) || null;
    let dim = resolveKey((v.rowDims || [])[0]) || keys.find(k => k !== meas && !isNumCol(k)) || null;
    // A color/series dimension (colDim, a 2nd row dimension, or — for sqlMode —
    // any remaining category column) means a breakdown chart: forecast EACH
    // series independently, matching AI Explore.
    let seriesDim = resolveKey(v.colDim) || resolveKey((v.rowDims || [])[1])
      || keys.find(k => k !== dim && k !== meas && !isNumCol(k)) || null;
    if (!dim || !meas) { toast.error('Need a time dimension and a measure to forecast'); return; }
    const measLabel = ((v.valueMeasures || [])[0]) || resolveLabel(allFields, meas) || meas;

    if (seriesDim && seriesDim !== dim) {
      const periodsAll = [...new Set(rows.map(r => String(r[dim])))].sort();
      const bySeries = {};
      rows.forEach(r => {
        const k = String(r[seriesDim]); const p = String(r[dim]);
        if (!k || k === 'null' || k === 'undefined') return;
        (bySeries[k] = bySeries[k] || {})[p] = (bySeries[k][p] || 0) + Number(r[meas] || 0);
      });
      if (periodsAll.length < 4) { toast.error('Need at least 4 time periods to forecast'); return; }
      setFcDialog({ title: v.title || meas, forecast: null });
      try {
        const future = futureLabels(periodsAll, 6);
        const groups = [];
        let statsAv = true;
        for (const [name, pmap] of Object.entries(bySeries)) {
          const vals = periodsAll.map(p => pmap[p] ?? 0);
          if (vals.filter(x => x).length < 4) continue;
          const res = await api.forecastSeries({ values: vals, periods: periodsAll, horizon: 6, method: 'auto' });
          statsAv = res.statsmodels_available;
          const gseries = [
            ...periodsAll.map((p, i) => ({ period: p, type: 'history', value: vals[i] })),
            ...res.forecast.map((f, i) => ({ period: future[i], type: 'forecast', value: f.value, lower: f.lower, upper: f.upper })),
          ];
          groups.push({ name, model: res.model, reason: res.reason, swing_note: res.swing_note, total: vals.reduce((a, b) => a + b, 0), series: gseries });
        }
        if (!groups.length) { toast.error('Not enough data per series to forecast'); setFcDialog(null); return; }
        groups.sort((a, b) => b.total - a.total);
        setFcDialog({ title: v.title || meas, forecast: {
          measure_label: measLabel, unit: '', multi: true,
          breakdown_label: resolveLabel(allFields, seriesDim) || seriesDim.replace(/_/g, ' '),
          horizon: 6, periods: [...periodsAll, ...future], boundary_period: periodsAll[periodsAll.length - 1],
          groups, algorithms: fcAlgos, statsmodels_available: statsAv,
        } });
      } catch (e) { toast.error(e.message || 'Forecast failed'); setFcDialog(null); }
      return;
    }

    const periods = rows.map(r => String(r[dim]));
    const values = rows.map(r => Number(r[meas])).filter(x => !Number.isNaN(x));
    if (values.length < 4) { toast.error('Need a time dimension, a measure, and 4+ data points to forecast'); return; }
    setFcDialog({ title: v.title || meas, forecast: null });
    try {
      const res = await api.forecastSeries({ values, periods, horizon: 6, method: 'auto' });
      const labels = futureLabels(periods, res.forecast.length);
      const series = [
        ...periods.map((p, i) => ({ period: p, type: 'history', value: values[i] })),
        ...res.forecast.map((f, i) => ({ period: labels[i], type: 'forecast', value: f.value, lower: f.lower, upper: f.upper })),
      ];
      setFcDialog({ title: v.title || meas, forecast: { ...res, series, measure_label: measLabel, algorithms: fcAlgos, statsmodels_available: res.statsmodels_available } });
    } catch (e) { toast.error(e.message || 'Forecast failed'); setFcDialog(null); }
  };

  // Renders a single visual's card (title + body) for the paginated Report view.
  // Reuses the same per-type renderers as the dashboard grid.
  const renderVisualBody = (v) => {
    const vizType = v.vizType || report.visualization_type || 'table';
    const vResult = visualResults[v.id] || null;
    const guardNote = isAdmin ? (vResult?.guard_note || vResult?.result?.guard_note || null) : null;
    const vDims = v.rowDims || [];
    const vCol = v.colDim || '';
    const chartData = transformDataForView(vResult?.data, v, config.dateParts || {});
    const isChart = !['kpi', 'ai_text', 'image', 'rich_text', 'shape', 'table', 'pivot', 'forecast', 'sentinel_summary'].includes(vizType);
    return (
      <div className="h-full w-full flex flex-col" data-testid={`report-visual-${v.id}`}>
        {guardNote && (
          <div className="flex items-start gap-1.5 mb-1 px-2 py-1 rounded-md bg-amber-500/10 border border-amber-500/25 flex-shrink-0" data-testid={`viewer-guard-note-${v.id}`} title={guardNote}>
            <ShieldCheck className="w-3 h-3 text-amber-500 mt-0.5 flex-shrink-0" />
            <p className="text-[10px] leading-snug text-amber-700 dark:text-amber-300">{guardNote}</p>
          </div>
        )}
        {v.title && !['image', 'shape'].includes(vizType) && (
          <div className="flex items-center justify-between gap-2 mb-1 flex-shrink-0">
            <p className="text-xs font-heading font-semibold text-gray-800">{v.title}</p>
            {isChart && (v.rowDims || []).length >= 1 && (v.rowDims || []).length <= 2 && (v.valueMeasures || []).length >= 1 && (
              <button onClick={() => openForecast(v)} className="flex items-center gap-1 text-[10px] text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded px-1.5 py-0.5 print:hidden" data-testid={`forecast-visual-${v.id}`} title="Forecast future periods">
                <TrendingUp className="w-3 h-3" /> Forecast
              </button>
            )}
          </div>
        )}
        <div className={`flex-1 min-h-0 ${['table', 'pivot', 'ai_text', 'rich_text'].includes(vizType) ? 'overflow-auto' : 'overflow-hidden'}`}>
          {vizType === 'kpi' ? (() => {
            const vm = v.valueMeasures || [];
            const val = vResult?.data?.[0]?.[vm[0]] ?? null;
            const fmtVal = val !== null ? formatValue(val, vm[0]) : '--';
            const label = resolveLabel(allFields, vm[0]) || 'Value';
            return (
              <div className="flex flex-col justify-center h-full px-2">
                <p className="text-[11px] text-gray-500 uppercase tracking-wider">{label}</p>
                <p className="text-3xl font-heading font-bold text-gray-900 mt-1">{fmtVal}</p>
                {v.kpiIndicator && <p className="text-[11px] mt-2 text-emerald-600">{v.kpiIndicator}</p>}
              </div>
            );
          })() : vizType === 'ai_text' ? (
            <div className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed p-1">{viewAiContents[v.id] || v.aiContent || config.aiContents?.[v.id] || ''}</div>
          ) : vizType === 'image' ? (
            <div className="h-full flex items-center justify-center">{v.imageUrl ? <img src={v.imageUrl} alt={v.title} className={'max-w-full max-h-full object-' + (v.imageFit || 'contain')} /> : null}</div>
          ) : vizType === 'rich_text' ? (
            <div style={{ fontSize: v.textSize || 14, color: v.textColor || '#111', textAlign: v.textAlign || 'left', fontWeight: v.textBold ? 'bold' : 'normal', fontStyle: v.textItalic ? 'italic' : 'normal', whiteSpace: 'pre-wrap' }}>{v.textContent || ''}</div>
          ) : vizType === 'shape' ? (
            <div className="h-full w-full">
              {(v.shapeType === 'rectangle' || !v.shapeType) && <div className="w-full h-full" style={{ background: v.shapeFill || '#3b82f6', opacity: v.shapeOpacity || 0.3, border: `2px solid ${v.shapeBorder || '#1e40af'}`, borderRadius: v.shapeBorderRadius || 12 }} />}
              {v.shapeType === 'circle' && <div className="w-full h-full" style={{ background: v.shapeFill || '#3b82f6', opacity: v.shapeOpacity || 0.3, border: `2px solid ${v.shapeBorder || '#1e40af'}`, borderRadius: '50%' }} />}
            </div>
          ) : (visualResults[v.id]?.comparisonResult?.data?.length > 0) ? (
            <ComparisonTableRenderer result={visualResults[v.id].comparisonResult} vizConfig={savedVizConfigs[v.id] || {}} formatValue={fmtForVisual(v.id)} allFields={allFields} />
          ) : vizType === 'table' ? (
            <TableRenderer result={chartData ? { ...vResult, data: chartData } : vResult} vizConfig={{ ...(savedVizConfigs[v.id] || {}), ...(viewVizOverrides[v.id] || {}) }} formatValue={fmtForVisual(v.id)} allFields={allFields} fieldOverrides={savedFieldOverrides} rowDims={vDims} onDrill={(val) => handleCrossFilter(val, v.id, v)} onVizConfigChange={(cfg) => handleTablePref(v.id, cfg)} />
          ) : vizType === 'pivot' ? (
            <PivotTableRenderer pivotResult={visualResults[v.id]?.pivotResult} vizConfig={savedVizConfigs[v.id] || {}} formatValue={fmtForVisual(v.id)} allFields={allFields} onDrill={(val) => handleCrossFilter(val, v.id, v)} />
          ) : vizType === 'forecast' ? (
            <ForecastTile snapshot={v.forecastSnapshot} meta={v.forecastMeta} formatValue={fmtForVisual(v.id)} sentinel={v.sentinel} />
          ) : vizType === 'sentinel_summary' ? (
            <SentinelSummaryTile />
          ) : isChart ? (
            <ChartRenderer vizType={vizType} data={chartData} rowDims={vDims} colDim={vCol} showGrid={config.showGrid !== false} showLegend={config.showLegend !== false} vizConfig={savedVizConfigs[v.id] || {}} formatValue={fmtForVisual(v.id)} allFields={allFields} fieldOverrides={savedFieldOverrides} onDrill={(val) => handleCrossFilter(val, v.id, v)} secondYAxis={savedSecondYAxis[v.id]} themeColors={savedThemeColors} valueMeasures={v.valueMeasures} />
          ) : null}
        </div>
      </div>
    );
  };

  const isReportLayout = layoutMode === 'report';

  if (loading) return <div className="flex items-center justify-center h-full"><Loader2 className="w-8 h-8 text-primary animate-spin" /></div>;
  if (!report) return <div className="flex flex-col items-center justify-center h-full"><p className="text-sm text-muted-foreground">Report not found</p><Button variant="outline" onClick={() => navigate('/reports')} className="mt-3 rounded-lg">Back to Reports</Button></div>;

  const config = report.config || {};
  const visuals = getVisuals();
  const dimensions = allFields.filter(f => f.semantic_type === 'dimension');
  const savedSecondYAxis = config.secondYAxisConfig || {};
  const savedThemeColors = config.reportTheme?.colors || null;
  const savedVizConfigs = config.visualConfigs || {};
  const savedPages = config.pages || [{ id: 'page_1', name: 'Page 1' }];
  const savedPageVisuals = config.pageVisuals || null;
  const pageNavStyle = config.pageNavStyle || 'tabs';
  const activeVisuals = savedPageVisuals ? (savedPageVisuals[viewPageId] || visuals) : visuals;
  const printReady = printMode && (((activeVisuals || []).length > 0 && (activeVisuals || []).every(v => visualResults[v.id] !== undefined)) || printSettle);

  // Lets the browser paint the "exporting" UI and service input between the
  // heavy html2canvas calls, so a multi-page export never freezes the tab into
  // a "Page Unresponsive" dialog. Each capture becomes its own task with a
  // breather in between instead of one giant uninterruptible block.
  const yieldToMain = () => new Promise(resolve => requestAnimationFrame(() => setTimeout(resolve, 0)));

  // User hit "Cancel" mid-export. html2canvas can't be aborted inside a single
  // capture, but the loops check this flag before each page/capture and the
  // server-side fetch is aborted — so the export stops without a page reload.
  const cancelExport = () => {
    exportCancelRef.current = true;
    try { exportAbortRef.current?.abort(); } catch { /* no-op */ }
    setExportProgress(p => ({ ...p, label: 'Cancelling…' }));
  };
  const throwIfCancelled = () => {
    if (exportCancelRef.current) { const e = new Error('__export_cancelled__'); e.cancelled = true; throw e; }
  };

  // Stamp a branded footer (logo-aligned brand name · generated date · page
  // numbers) onto every page of a client-generated PDF for client-ready output.
  const stampBrandedFooter = (pdf) => {
    try {
      const brand = getBrandName();
      const when = new Date().toLocaleDateString();
      const total = pdf.internal.getNumberOfPages();
      const pw = pdf.internal.pageSize.getWidth();
      const ph = pdf.internal.pageSize.getHeight();
      for (let i = 1; i <= total; i++) {
        pdf.setPage(i);
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(8);
        pdf.setTextColor(150);
        pdf.setDrawColor(225);
        pdf.line(28, ph - 24, pw - 28, ph - 24);
        pdf.text(`${brand} · ${when}`, 28, ph - 12);
        pdf.text(`Page ${i} of ${total}`, pw - 28, ph - 12, { align: 'right' });
      }
    } catch { /* footer is cosmetic */ }
  };

  const _captureToPdf = async (pdf, isFirstDoc) => {
    throwIfCancelled();
    await yieldToMain();
    const { default: html2canvas } = await import('html2canvas');
    const el = canvasRef.current;
    const restore = await inlineImagesForCapture(el);
    let canvas;
    try {
      canvas = await html2canvas(el, { backgroundColor: (document.documentElement.classList.contains('dark') ? '#09090b' : '#ffffff'), scale: 2, logging: false, useCORS: true, imageTimeout: 15000 });
    } finally { restore(); }
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    const M = 28;
    const name = report.name || 'Report';
    if (!isFirstDoc) pdf.addPage();
    pdf.setFont('helvetica', 'bold'); pdf.setFontSize(14); pdf.setTextColor(30);
    if (themeLogo) {
      try {
        const img = new Image(); img.crossOrigin = 'anonymous';
        await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = themeLogo; });
        const lh = 24, lw = lh * (img.width / img.height || 1);
        const c2 = document.createElement('canvas'); c2.width = img.width; c2.height = img.height;
        c2.getContext('2d').drawImage(img, 0, 0);
        let lx = M;
        if (themeLogoPos === 'center') lx = (pageW - lw) / 2;
        else if (themeLogoPos === 'right') lx = pageW - M - lw;
        pdf.addImage(c2.toDataURL('image/png'), 'PNG', lx, M - 6, lw, lh);
        pdf.text(name, themeLogoPos === 'left' ? M + lw + 12 : M, M + 10);
      } catch { pdf.text(name, M, M + 10); }
    } else {
      pdf.text(name, M, M + 10);
    }
    const y = M + 34;
    const availW = pageW - M * 2;
    const imgH = (canvas.height * availW) / canvas.width;
    if (imgH <= pageH - y - M) {
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', M, y, availW, imgH);
    } else {
      const pxPerPt = canvas.width / availW;
      const sliceHpx = (pageH - y - M) * pxPerPt;
      let offset = 0; let first = true;
      while (offset < canvas.height) {
        const slice = document.createElement('canvas');
        slice.width = canvas.width; slice.height = Math.min(sliceHpx, canvas.height - offset);
        slice.getContext('2d').drawImage(canvas, 0, offset, canvas.width, slice.height, 0, 0, canvas.width, slice.height);
        if (!first) pdf.addPage();
        const yy = first ? y : M;
        pdf.addImage(slice.toDataURL('image/png'), 'PNG', M, yy, availW, (slice.height / pxPerPt));
        offset += slice.height; first = false;
      }
    }
  };

  // Shared headless-Chromium render. Returns true if it produced & downloaded a
  // PDF. Aborts cleanly when the user cancels. Used for BOTH the pixel-perfect
  // report layout AND (single-page) dashboards so the heavy render runs on the
  // server instead of blocking the browser with html2canvas.
  const attemptServerPdf = async (queryStr = '', pixel = false) => {
    const csrf = (document.cookie.match(/(?:^|; )csrf_token=([^;]+)/) || [])[1];
    const wsid = (() => { try { return localStorage.getItem('inzara.active_workspace_id'); } catch { return null; } })();
    const brandParam = `brand=${encodeURIComponent(getBrandName())}${pixel ? '&pixel=1' : ''}`;
    const sep = queryStr ? '&' : '?';
    let u = `${process.env.REACT_APP_BACKEND_URL}/api/reports/${reportId}/server-pdf${queryStr}${sep}${brandParam}`;
    const hdrs = {};
    if (csrf) hdrs['X-CSRF-Token'] = decodeURIComponent(csrf);
    if (wsid) hdrs['X-Workspace-Id'] = wsid;
    const ctrl = new AbortController();
    exportAbortRef.current = ctrl;
    const resp = await fetch(u, { method: 'POST', credentials: 'include', headers: hdrs, signal: ctrl.signal });
    if (!resp.ok) { console.warn('server-side PDF unavailable, falling back', resp.status); return false; }
    const blob = await resp.blob();
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${(report.name || 'report').replace(/[^a-z0-9]+/gi, '_')}.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 4000);
    return true;
  };

  const handleExportPdf = async (pageIds = null, opts = {}) => {
    if (!canvasRef.current) return;
    exportCancelRef.current = false;
    exportAbortRef.current = null;
    setExporting(true);
    setExportProgress({ current: 0, total: 0, label: 'Preparing export…' });
    const original = viewPageId;
    let progressToast = null;
    try {
      const { default: jsPDF } = await import('jspdf');
      // Report layout: export each rendered paper page as its own PDF page so
      // the output matches the on-screen paginated report (header/footer +
      // spanning tables broken across pages).
      if (isReportLayout) {
        // Prefer pixel-perfect server-side render (headless Chromium) so charts,
        // maps and images always appear regardless of browser image rules.
        try {
          setExportProgress({ current: 0, total: 0, label: 'Rendering on server…' });
          const q = (rangeTotal > 1) ? `?page_from=${rangeFrom}&page_to=${rangeTo}` : '';
          if (await attemptServerPdf(q)) { toast.success('Exported PDF'); return; }
        } catch (e) {
          if (e.name === 'AbortError' || exportCancelRef.current) throw Object.assign(new Error('__export_cancelled__'), { cancelled: true });
          console.warn('server-side PDF error, using in-browser export', e);
        }
        const { default: html2canvas } = await import('html2canvas');
        let pageEls = Array.from(canvasRef.current.querySelectorAll('.report-print-page'));
        if (!pageEls.length) throw new Error('no pages');
        const explicitRange = rangeTotal > 1;
        const from = explicitRange ? Math.max(1, Math.min(rangeFrom, pageEls.length)) : 1;
        const to = explicitRange ? Math.max(from, Math.min(rangeTo, pageEls.length)) : pageEls.length;
        pageEls = pageEls.slice(from - 1, to);
        const portrait = (config.reportOrientation || 'portrait') === 'portrait';
        const fmt = (config.reportPageSize === 'a4') ? 'a4' : (config.reportPageSize === 'legal') ? 'legal' : 'letter';
        const pdf = new jsPDF({ orientation: portrait ? 'portrait' : 'landscape', unit: 'pt', format: fmt });
        const pw = pdf.internal.pageSize.getWidth();
        const ph = pdf.internal.pageSize.getHeight();
        const restore = await inlineImagesForCapture(canvasRef.current);
        progressToast = toast.loading(`Rendering page 1 of ${pageEls.length}…`);
        try {
          for (let i = 0; i < pageEls.length; i++) {
            throwIfCancelled();
            setExportProgress({ current: i, total: pageEls.length, label: `Rendering page ${i + 1} of ${pageEls.length}…` });
            toast.loading(`Rendering page ${i + 1} of ${pageEls.length}…`, { id: progressToast });
            await yieldToMain();
            const c = await html2canvas(pageEls[i], { backgroundColor: '#ffffff', scale: 2, logging: false, useCORS: true, imageTimeout: 15000 });
            if (i > 0) pdf.addPage();
            // JPEG (q=0.92) keeps table text crisp while cutting the file size
            // many-fold vs PNG — a dense full-page PNG is ~10 MB each.
            pdf.addImage(c.toDataURL('image/jpeg', 0.92), 'JPEG', 0, 0, pw, ph);
          }
        } finally { restore(); }
        pdf.save(`${(report.name || 'report').replace(/[^a-z0-9]+/gi, '_')}.pdf`);
        toast.success('Exported PDF', { id: progressToast });
        return;
      }
      const ids = (pageIds && pageIds.length) ? pageIds : [viewPageId];
      // Pixel-perfect dashboard → headless-Chromium render of the on-screen grid
      // (opt-in toggle). Otherwise, single-page dashboards also offload to the
      // server. Multi-page non-pixel dashboards use the client path (it flips
      // through the in-app pages the server render can't).
      const singlePageDash = ((report?.config?.pages || []).length <= 1) && ids.length <= 1;
      if (opts.pixel && singlePageDash) {
        setExportProgress({ current: 0, total: 0, label: 'Rendering pixel-perfect PDF on server…' });
        if (await attemptServerPdf('', true)) { toast.success('Exported PDF'); emitCoachEvent('report_exported', 'report-export-button'); return; }
        toast.error('Pixel-perfect render failed — falling back to standard export');
      } else if (singlePageDash) {
        try {
          setExportProgress({ current: 0, total: 0, label: 'Rendering on server…' });
          if (await attemptServerPdf('')) { toast.success('Exported PDF'); emitCoachEvent('report_exported', 'report-export-button'); return; }
        } catch (e) {
          if (e.name === 'AbortError' || exportCancelRef.current) throw Object.assign(new Error('__export_cancelled__'), { cancelled: true });
          console.warn('server-side dashboard PDF error, using in-browser export', e);
        }
      }
      const pdf = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      progressToast = toast.loading(ids.length > 1 ? `Rendering page 1 of ${ids.length}…` : 'Rendering PDF…');
      for (let i = 0; i < ids.length; i++) {
        throwIfCancelled();
        setExportProgress({ current: i, total: ids.length, label: ids.length > 1 ? `Rendering page ${i + 1} of ${ids.length}…` : 'Rendering PDF…' });
        if (ids.length > 1) toast.loading(`Rendering page ${i + 1} of ${ids.length}…`, { id: progressToast });
        if (ids[i] !== viewPageId) { setViewPageId(ids[i]); await new Promise(r => setTimeout(r, 900)); }
        await _captureToPdf(pdf, i === 0);
      }
      stampBrandedFooter(pdf);
      pdf.save(`${(report.name || 'report').replace(/[^a-z0-9]+/gi, '_')}.pdf`);
      toast.success('Exported PDF', { id: progressToast });
      emitCoachEvent('report_exported', 'report-export-button');
    } catch (err) {
      if (err && err.cancelled) {
        toast.info('Export cancelled', progressToast ? { id: progressToast } : undefined);
      } else {
        toast.error('Could not export PDF', progressToast ? { id: progressToast } : undefined);
        console.error('report pdf export', err);
      }
    } finally {
      if (viewPageId !== original) setViewPageId(original);
      exportAbortRef.current = null;
      exportCancelRef.current = false;
      setExportProgress({ current: 0, total: 0, label: '' });
      setExporting(false);
      setExportPickerOpen(false);
    }
  };

  const handleExportPng = async () => {
    if (!canvasRef.current) return;
    exportCancelRef.current = false;
    setExporting(true);
    setExportProgress({ current: 0, total: 1, label: 'Rendering image…' });
    try {
      await yieldToMain();
      throwIfCancelled();
      const { default: html2canvas } = await import('html2canvas');
      const restore = await inlineImagesForCapture(canvasRef.current);
      let canvas;
      try {
        canvas = await html2canvas(canvasRef.current, { backgroundColor: (document.documentElement.classList.contains('dark') ? '#09090b' : '#ffffff'), scale: 2, logging: false, useCORS: true, imageTimeout: 15000 });
      } finally { restore(); }
      throwIfCancelled();
      const link = document.createElement('a');
      link.download = `${(report?.name || 'insight').replace(/\s+/g, '_')}.png`;
      link.href = canvas.toDataURL('image/png');
      link.click();
      toast.success('Exported PNG');
      emitCoachEvent('report_exported', 'report-export-button');
    } catch (err) {
      if (err && err.cancelled) { toast.info('Export cancelled'); }
      else { toast.error('Could not export PNG'); console.error('report png export', err); }
    } finally {
      exportCancelRef.current = false;
      setExportProgress({ current: 0, total: 0, label: '' });
      setExporting(false);
      setExportPickerOpen(false);
    }
  };

  const runExport = () => {
    if (exportFormat === 'png') return handleExportPng();
    const pages = report?.config?.pages || [];
    const multi = pages.length > 1;
    const ids = multi ? (report?.config?.pages || []).filter(p => (exportPageIds || []).includes(p.id)).map(p => p.id) : null;
    return handleExportPdf(ids);
  };

  const onExportClick = () => {
    const pages = report?.config?.pages || [];
    setExportPageIds(pages.map(p => p.id));
    setExportFormat('pdf');
    if (isReportLayout) {
      const total = canvasRef.current?.querySelectorAll('.report-print-page').length || 1;
      setRangeTotal(total);
      setRangeFrom(1);
      setRangeTo(total);
    }
    setExportPickerOpen(true);
  };

  return (
    <div ref={pageRef} onMouseMove={onPageMouseMove} className="h-full flex flex-col overflow-hidden relative" data-testid="report-view-page"
      onContextMenuCapture={e => {
        e.preventDefault(); e.stopPropagation();
        let el = e.target;
        while (el && el !== document.body) {
          if (el.getAttribute && el.getAttribute('data-visual-id')) {
            setContextMenu({ x: e.clientX, y: e.clientY, visualId: el.getAttribute('data-visual-id') });
            return;
          }
          el = el.parentNode;
        }
      }}
    >
      {/* Subtle dashboard-level "updating…" indicator while visuals reload */}
      {loadingVisuals.size > 0 && (
        <div className="absolute top-3 right-3 z-40 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-medium text-muted-foreground border border-border/50 shadow-sm animate-in fade-in"
          style={{ background: 'var(--glass-bg)', backdropFilter: 'blur(12px)' }}
          data-testid="dashboard-updating-indicator">
          <Loader2 className="w-3 h-3 animate-spin text-primary" /> Updating…
        </div>
      )}
      {/* Header */}
      <div className="report-view-toolbar relative z-20 px-6 py-4 border-b border-border/50 flex flex-col gap-3" style={{ background: 'var(--glass-bg)', backdropFilter: 'blur(12px)' }}>
        {/* Title row — spans the full width across the top */}
        <div className="flex items-center gap-3 min-w-0">
          {!chromeless && <Button variant="ghost" size="icon" onClick={closeAndLeave} className="rounded-lg w-8 h-8 flex-shrink-0" data-testid="report-view-back"><ArrowLeft className="w-4 h-4" /></Button>}
          {themeLogo && themeLogoPos === 'left' ? <img src={themeLogo} alt="" className="h-8 max-w-[120px] object-contain flex-shrink-0" data-testid="report-header-logo" /> : null}
          <div className="min-w-0 flex-1">
            <h1 className="text-xl font-heading font-bold text-foreground flex items-center gap-2 min-w-0">
              <span className="truncate" title={report.name}>{report.name}</span>
              {report._version != null && <span className="text-sm font-mono font-normal text-muted-foreground flex-shrink-0" data-testid="viewer-version-label">v{report._version}</span>}
              {report._stage === 'production' && <Badge variant="outline" className="text-[9px] text-emerald-400 border-emerald-500/40 bg-emerald-500/10 flex-shrink-0" data-testid="viewer-stage-badge">Production</Badge>}
              {report._stage === 'dev' && <Badge variant="outline" className="text-[9px] text-amber-400 border-amber-500/40 bg-amber-500/10 flex-shrink-0" data-testid="viewer-stage-badge" title="This insight has not been promoted to Production yet — showing the latest Development version.">Development</Badge>}
            </h1>
            {report.description && <p className="text-xs text-muted-foreground mt-0.5 truncate" title={report.description}>{report.description}</p>}
          </div>
          {themeLogo && themeLogoPos === 'center' ? <img src={themeLogo} alt="" className="absolute left-1/2 -translate-x-1/2 top-4 h-8 max-w-[120px] object-contain pointer-events-none" data-testid="report-header-logo" /> : null}
        </div>
        {/* Actions row */}
        {!chromeless && <div className="flex items-center gap-2 flex-wrap">
          {themeLogo && themeLogoPos === 'right' ? <img src={themeLogo} alt="" className="h-8 max-w-[120px] object-contain mr-1" data-testid="report-header-logo" /> : null}
          <Button variant="outline" size="sm" onClick={() => refreshAllVisuals(crossFilter, globalFilterValues)} disabled={loadingVisuals.size > 0} className="rounded-lg text-xs h-8 gap-1.5 print:hidden" data-testid="report-refresh-data-btn" title="Refresh data — rerun all queries"><RefreshCw className={'w-3 h-3 ' + (loadingVisuals.size > 0 ? 'animate-spin' : '')} /> Refresh</Button>
          <Button variant="outline" size="sm" onClick={() => setLayoutMode(m => m === 'report' ? 'dashboard' : 'report')} className="rounded-lg text-xs h-8 gap-1.5 print:hidden" data-testid="layout-toggle-btn" title={isReportLayout ? 'Switch to Dashboard view' : 'Switch to Report view'}>
            {isReportLayout ? <><LayoutGrid className="w-3 h-3" /> Dashboard view</> : <><FileStack className="w-3 h-3" /> Report view</>}
          </Button>
          <Button variant="outline" size="sm" onClick={() => setFrameworkExportOpen(true)} disabled={exporting} className="rounded-lg text-xs h-8 gap-1.5 print:hidden" data-testid="report-export-pdf-btn" data-coach-target="report-export-button">{exporting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />} Export</Button>
          <Button variant="outline" size="sm" onClick={() => setScheduleOpen(true)} className="rounded-lg text-xs h-8 gap-1.5 print:hidden" data-testid="report-schedule-btn"><CalendarClock className="w-3 h-3" /> Schedule</Button>
          <Button variant="outline" size="sm" onClick={() => navigate(`/reports/builder?id=${reportId}`)} className="rounded-lg text-xs h-8 gap-1.5" data-testid="report-edit-btn"><Pencil className="w-3 h-3" /> Edit in Builder</Button>
          {canCompareSource && (
            <Button variant="outline" size="sm" onClick={() => setCompareOpen(true)} className="rounded-lg text-xs h-8 gap-1.5" data-testid="compare-source-btn"><FileText className="w-3 h-3" /> Compare to source</Button>
          )}
          <Button variant="outline" size="sm" onClick={async () => { try { await api.updateUserProfile({ home_dashboard_id: reportId }); toast.success('Set as Home Dashboard'); } catch (err) { toast.error(err.message); } }} className="rounded-lg text-xs h-8 gap-1.5" data-testid="set-as-home-btn"><Home className="w-3 h-3" /> Set as Home</Button>
          <PresenceViewers objectType={assetType} objectId={reportId} following={following?.uid} onFollow={onFollowViewer} onStopFollow={onStopFollowing} />
          <Button variant="outline" size="sm" onClick={shareMyView} className="rounded-lg text-xs h-8 gap-1.5 print:hidden" data-testid="share-view-btn"><Users className="w-3 h-3" /> Share view</Button>
          <Button variant="outline" size="sm" onClick={() => setShareOpen(true)} className="rounded-lg text-xs h-8 gap-1.5" data-testid="share-preview-btn"><Share2 className="w-3 h-3" /> Share preview</Button>
          <Button variant="outline" size="sm" onClick={() => setUsageOpen(true)} className="rounded-lg text-xs h-8 gap-1.5" data-testid="report-usage-btn"><BarChart2 className="w-3 h-3" /> Usage</Button>
          <Button variant="outline" size="sm" onClick={() => setRightPanelOpen(!rightPanelOpen)} className="rounded-lg text-xs h-8 gap-1.5" data-testid="toggle-right-panel"><MessageSquare className="w-3 h-3" /> {rightPanelOpen ? 'Hide Panel' : 'Activity'}</Button>
        </div>}
      </div>

      {following && leaderCursor && (
        <div className="pointer-events-none absolute z-30 transition-all duration-75" style={{ left: `${(leaderCursor.x || 0) / 10}%`, top: `${(leaderCursor.y || 0) / 10}%` }} data-testid="follow-cursor">
          <MousePointer2 className="w-4 h-4 text-cyan-400 fill-current drop-shadow" />
          <span className="ml-3 -mt-1 inline-block px-1.5 py-0.5 rounded bg-cyan-500 text-white text-[9px] font-semibold whitespace-nowrap">{leaderCursor.name || following.name}</span>
        </div>
      )}

      {following && (
        <div className="px-4 py-1.5 flex items-center justify-between bg-cyan-500/15 border-b border-cyan-500/30 text-cyan-200 text-xs print:hidden" data-testid="follow-banner">
          <span className="flex items-center gap-1.5"><Radio className="w-3.5 h-3.5 animate-pulse" /> Following <strong>{following.name}</strong> — your view syncs to theirs (data stays under your permissions)</span>
          <Button variant="ghost" size="sm" onClick={onStopFollowing} className="h-6 text-[11px] text-cyan-100 hover:bg-cyan-500/20" data-testid="stop-follow-btn">Stop following</Button>
        </div>
      )}

      {/* Global Filters + Cross-Filter Bar */}
      <div className="report-view-toolbar px-4 py-2 border-b border-border/50 flex items-center gap-2 overflow-x-auto" style={{ background: 'var(--glass-bg)', backdropFilter: 'blur(12px)' }} data-coach-target="reports-filter-panel">        <Filter className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
        <span className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold flex-shrink-0">Filters</span>
        {/* Global filter dimension picker — searchable, grouped by source table */}
        <FilterFieldPicker
          testid="view-add-filter"
          dims={dimensions.filter(d => !globalFilterDims.includes(d.name))}
          onPick={v => { if (v && !globalFilterDims.includes(v)) { setGlobalFilterDims(prev => [...prev, v]); emitCoachEvent('filter_applied', 'reports-filter-panel'); } }}
        />
        {globalFilterDims.map(dim => (
          <div key={dim} className="flex items-center gap-1 flex-shrink-0">
            <SlicerControl dimension={dim} values={sortSlicerValues(globalSlicerValues[dim] || [], getSlicerSetting(slicerSettings, dim).sortOrder)} selected={globalFilterValues[dim]} onChange={val => { setGlobalFilterValues(p => ({ ...p, [dim]: val })); if (getSlicerSetting(slicerSettings, dim).defaultSelection?.type === 'remember') writeRememberedSelection(reportId, dim, val); }} isDate={isDateField(allFields.find(f => f.name === dim))} fiscalStartMonth={fiscalStartMonth} settings={getSlicerSetting(slicerSettings, dim)} />
            <button onClick={() => { setGlobalFilterDims(prev => prev.filter(d => d !== dim)); setGlobalFilterValues(prev => { const c = { ...prev }; delete c[dim]; return c; }); }} className="text-muted-foreground/70 hover:text-destructive"><X className="w-3 h-3" /></button>
          </div>
        ))}
        {/* Page-scope filters — affect only the current page's visuals */}
        {savedPages.length > 1 && (
          <div className="flex items-center gap-2 flex-shrink-0 pl-2 ml-1 border-l border-border/40" data-testid="viewer-page-filters">
            <span className="text-[9px] text-amber-500 uppercase tracking-wider font-semibold flex items-center gap-1 flex-shrink-0"><span className="w-1.5 h-1.5 rounded-full bg-amber-400" />This page</span>
            <FilterFieldPicker
              testid="view-add-page-filter"
              placeholder="+ Page filter"
              dims={dimensions.filter(d => !(pageFilterDims[viewPageId] || []).includes(d.name))}
              onPick={v => { if (v) setPageFilterDims(prev => { const cur = prev[viewPageId] || []; return cur.includes(v) ? prev : { ...prev, [viewPageId]: [...cur, v] }; }); }}
            />
            {(pageFilterDims[viewPageId] || []).map(dim => (
              <div key={dim} className="flex items-center gap-1 flex-shrink-0">
                <SlicerControl dimension={dim} values={sortSlicerValues(globalSlicerValues[dim] || [], getSlicerSetting(slicerSettings, dim).sortOrder)} selected={(pageFilterValues[viewPageId] || {})[dim]} onChange={val => { setPageFilterValues(prev => ({ ...prev, [viewPageId]: { ...(prev[viewPageId] || {}), [dim]: val } })); if (getSlicerSetting(slicerSettings, dim).defaultSelection?.type === 'remember') writeRememberedSelection(reportId, dim, val); }} isDate={isDateField(allFields.find(f => f.name === dim))} fiscalStartMonth={fiscalStartMonth} settings={getSlicerSetting(slicerSettings, dim)} />
                <button onClick={() => { setPageFilterDims(prev => ({ ...prev, [viewPageId]: (prev[viewPageId] || []).filter(d => d !== dim) })); setPageFilterValues(prev => { const pv = { ...(prev[viewPageId] || {}) }; delete pv[dim]; return { ...prev, [viewPageId]: pv }; }); }} className="text-muted-foreground/70 hover:text-destructive"><X className="w-3 h-3" /></button>
              </div>
            ))}
          </div>
        )}
        {/* "As of" snapshot date — portaled Popover so it's never clipped by the
            toolbar's horizontal overflow (previously rendered behind the report). */}
        <div className="flex-shrink-0 ml-auto flex items-center gap-1.5">
          <SavedFilterViews
            reportId={reportId}
            getSelections={() => ({ globalFilterValues, pageFilterValues, slicerSelections })}
            onApply={(sel) => {
              setGlobalFilterValues(sel.globalFilterValues || {});
              setPageFilterValues(sel.pageFilterValues || {});
              setSlicerSelections(sel.slicerSelections || {});
            }}
          />
          <Popover open={asOfOpen} onOpenChange={setAsOfOpen}>
            <PopoverTrigger asChild>
              <button
                className="flex items-center gap-1 text-[9px] rounded-lg border border-border/40 bg-muted/20 px-2 py-1 text-foreground hover:border-primary/40 transition-colors"
                title="'As of' snapshot date — pins point-in-time measures (e.g. inforce counts) and to-date values to this date"
                data-testid="view-as-of-trigger"
              >
                <CalendarClock className="w-3 h-3 text-primary" />
                As of: <span className="font-medium">{globalFilterValues['__asof__'] || 'Latest'}</span>
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-60 p-2.5 z-[120]" data-testid="view-as-of-popover">
                <div className="flex items-center gap-1.5 mb-2 text-[11px] font-semibold text-foreground">
                  <CalendarClock className="w-3.5 h-3.5 text-primary" /> Set as of date
                </div>
                <div className="flex items-center gap-1.5 mb-2">
                  <button
                    onClick={() => { setGlobalFilterValues(prev => { const c = { ...prev }; delete c['__asof__']; return c; }); setAsOfOpen(false); }}
                    className={'flex-1 text-[10px] px-2 py-1.5 rounded-md border transition-colors ' + (!globalFilterValues['__asof__'] ? 'border-primary/60 bg-primary/15 text-foreground' : 'border-border/40 text-muted-foreground hover:border-primary/40')}
                    title="Use the latest available snapshot"
                    data-testid="view-as-of-latest"
                  >Latest</button>
                  <button
                    onClick={() => {
                      const d = new Date(); const me = new Date(d.getFullYear(), d.getMonth() + 1, 0);
                      const iso = `${me.getFullYear()}-${String(me.getMonth() + 1).padStart(2, '0')}-${String(me.getDate()).padStart(2, '0')}`;
                      setGlobalFilterValues(prev => ({ ...prev, __asof__: iso })); setAsOfOpen(false);
                    }}
                    className="flex-1 text-[10px] px-2 py-1.5 rounded-md border border-border/40 text-muted-foreground hover:border-primary/40 transition-colors"
                    title="End of the current month"
                    data-testid="view-as-of-monthend"
                  >Month end</button>
                </div>
                <label className="block text-[10px] text-muted-foreground">
                  Or pick a date
                  <input
                    type="date"
                    className="w-full mt-0.5 text-[11px] rounded-md bg-muted/30 border border-border/50 px-2 py-1 text-foreground focus:outline-none focus:ring-1 focus:ring-primary/30"
                    value={globalFilterValues['__asof__'] || ''}
                    onChange={e => {
                      const v = e.target.value;
                      setGlobalFilterValues(prev => { const c = { ...prev }; if (v) c['__asof__'] = v; else delete c['__asof__']; return c; });
                    }}
                    data-testid="view-as-of-date"
                  />
                </label>
            </PopoverContent>
          </Popover>
        </div>
        {crossFilter && (
          <Badge className="text-[8px] bg-indigo-500 text-white border-indigo-600 gap-1 flex-shrink-0">
            Cross-filter: {resolveLabel(allFields, crossFilter.field)} = {String(crossFilter.value)}
            <button onClick={() => setCrossFilter(null)}><X className="w-2.5 h-2.5" /></button>
          </Badge>
        )}
      </div>

      {/* Filters summary bar — compact strip of active filters, flagging locked ones */}
      {(() => {
        const chips = []; const seen = new Set();
        const add = (dim, val) => {
          if (seen.has(dim) || val == null || val === '' || (Array.isArray(val) && !val.length)) return;
          seen.add(dim);
          const st = getSlicerSetting(slicerSettings, dim);
          const label = st.customTitle || resolveLabel(allFields, dim);
          const txt = Array.isArray(val)
            ? (val.length > 2 ? `${val.slice(0, 2).join(', ')} +${val.length - 2}` : val.join(', '))
            : (val && typeof val === 'object' ? (val.start && val.end ? `${val.start} – ${val.end}` : (val.preset || 'range')) : String(val));
          chips.push({ dim, label, txt, locked: !!st.locked });
        };
        Object.entries(globalFilterValues || {}).forEach(([f, v]) => { if (!f.startsWith('__')) add(f, v); });
        Object.entries(pageFilterValues[viewPageId] || {}).forEach(([f, v]) => add(f, v));
        getVisuals().forEach(v => (v.filterDims || []).forEach(dim => add(dim, slicerSelections[v.id + '_' + dim])));
        if (!chips.length) return null;
        const clearDim = (dim) => {
          setGlobalFilterValues(p => { const n = { ...p }; delete n[dim]; return n; });
          setPageFilterValues(p => { const n = { ...p }; if (n[viewPageId]) { n[viewPageId] = { ...n[viewPageId] }; delete n[viewPageId][dim]; } return n; });
          setSlicerSelections(p => { const n = { ...p }; getVisuals().forEach(v => { if ((v.filterDims || []).includes(dim)) delete n[v.id + '_' + dim]; }); return n; });
        };
        const anyUnlocked = chips.some(c => !c.locked);
        return (
          <div className="flex items-center gap-1.5 flex-wrap px-4 py-1.5 border-b border-border/30 bg-muted/10 print:hidden" data-testid="filters-summary-bar">
            <Filter className="w-3 h-3 text-muted-foreground flex-shrink-0" />
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground font-semibold mr-1">Filters</span>
            {chips.map(c => (
              <span key={c.dim} className={`flex items-center gap-1 text-[10px] rounded-full px-2 py-0.5 border ${c.locked ? 'border-amber-500/40 bg-amber-500/10 text-amber-600 dark:text-amber-400' : 'border-border/50 bg-background text-foreground'}`} data-testid={`filter-chip-${c.dim}`}>
                {c.locked && <Lock className="w-2.5 h-2.5" data-testid={`filter-chip-lock-${c.dim}`} />}
                <span className="font-medium">{c.label}:</span> {c.txt}
                {!c.locked && <button onClick={() => clearDim(c.dim)} className="ml-0.5 hover:text-destructive" data-testid={`filter-chip-clear-${c.dim}`}><X className="w-2.5 h-2.5" /></button>}
              </span>
            ))}
            {anyUnlocked && (
              <button onClick={() => chips.filter(c => !c.locked).forEach(c => clearDim(c.dim))} className="text-[10px] text-muted-foreground hover:text-foreground underline ml-1" data-testid="filters-clear-all">Clear all</button>
            )}
          </div>
        );
      })()}


      {/* Data Quality Banner (shows when Quickstart auto-corrected column types) */}
      {config.dataQuality && (
        <DataQualityBanner
          dataQuality={config.dataQuality}
          onReview={() => navigate(`/semantic-models?model=${config.dataQuality.model_id}`)}
        />
      )}

      {/* Content */}
      <div ref={canvasRef} className={`report-view-canvas flex-1 overflow-auto ${isReportLayout ? (chromeless ? 'p-2' : 'p-6') + ' bg-muted/20' : (chromeless ? 'p-2' : 'p-6')}`} onClick={() => contextMenu && setContextMenu(null)}>
        {globalFilterValues['__asof__'] && (
          <div className="mb-3 text-sm font-heading font-semibold text-foreground/80" data-testid="report-as-of-header">
            As of {globalFilterValues['__asof__']}
          </div>
        )}
        {isReportLayout ? (
          <>
          <div className="relative">
          {loadingVisuals.size > 0 && Object.keys(visualResults).length === 0 && (
            <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-background/70 backdrop-blur-sm" data-testid="report-preview-loading">
              <Loader2 className="w-8 h-8 text-primary animate-spin" />
              <div className="text-sm text-muted-foreground">Running query… results will appear shortly</div>
            </div>
          )}
          <ReportPagedViewer
            config={config}
            reportName={report.name}
            themeLogo={themeLogo}
            visuals={activeVisuals}
            resultOf={(v) => visualResults[v.id] || null}
            resolveData={(v) => transformDataForView(visualResults[v.id]?.data, v, config.dateParts || {})}
            renderBody={renderVisualBody}
            formatValueFor={(vid) => fmtForVisual(vid)}
            vizConfigFor={(vid) => savedVizConfigs[vid] || {}}
            allFields={allFields}
          />
          </div>
          {printReady && (
            <div data-print-ready="1" style={{ display: 'none' }} />
          )}
          </>
        ) : (
        <div style={{ position: 'relative', minHeight: activeVisuals.filter(v => isObjectVisual(v.vizType)).reduce((max, v) => Math.max(max, (v.freePosition?.y || 0) + (v.freePosition?.h || 150) + 50), 0) || undefined }}>
          {discussions.length > 0 && (
            <div className="flex items-center gap-2 mb-4">
              <Badge className="text-[9px] bg-amber-500 text-white border-amber-600 gap-1 cursor-pointer" onClick={() => { setListDiscussions(discussions); setListOpen(true); }}>
                <MessageSquare className="w-3 h-3" /> {discussions.length} discussion{discussions.length > 1 ? 's' : ''} on this report
              </Badge>
            </div>
          )}
          {/* Page Navigation — author-chosen style, viewers see visible pages only */}
          <PageNavChrome style={pageNavStyle} pages={savedPages} activeId={viewPageId} onChange={setViewPageId} viewerMode storageKey={reportId}>
          {(() => {
            const gridVisuals = activeVisuals.filter(v => !isObjectVisual(v.vizType));
            const freeVis = activeVisuals.filter(v => isObjectVisual(v.vizType));
            const savedGridLayouts = config.gridLayouts;
              const defaultLayout = gridVisuals.map((v, i) => ({
              i: v.id, x: (i % 2) * 6, y: Math.floor(i / 2) * 4,
              w: gridVisuals.length === 1 ? 12 : 6, h: 4, minW: 3, minH: 2,
            }));
            // Every grid visual MUST have a layout item, otherwise react-grid-layout
            // renders it at its 1x1 default — the "tiny, inoperable tile" seen when a
            // visual (e.g. one added from AI Explore) isn't in the saved layout. Reuse
            // the saved item when present, else append a full-width default below the
            // lowest existing row so the new tile is visible and usable.
            let layout;
            if (savedGridLayouts?.lg && savedGridLayouts.lg.length) {
              const byId = Object.fromEntries(savedGridLayouts.lg.map(l => [l.i, l]));
              let nextY = savedGridLayouts.lg.reduce((m, l) => Math.max(m, (l.y || 0) + (l.h || 4)), 0);
              layout = gridVisuals.map((v) => {
                const saved = byId[v.id];
                if (saved) return { minW: 3, minH: 2, ...saved };
                const item = { i: v.id, x: 0, y: nextY, w: 12, h: 4, minW: 3, minH: 2 };
                nextY += 4;
                return item;
              });
            } else {
              layout = defaultLayout;
            }
            // In chromeless (Set-as-Home) mode, a lone visual spans the full width
            // so it fills the page instead of sitting in a half-width column.
            if (chromeless && gridVisuals.length === 1 && layout.length === 1) {
              layout = [{ ...layout[0], x: 0, w: 12 }];
            }
            // In chromeless mode, stretch a short dashboard so it fills the
            // available height instead of leaving empty space.
            const gridRows = Math.max(1, (layout || []).reduce((m, l) => Math.max(m, (l.y || 0) + (l.h || 4)), 0));
            const fillRowHeight = chromeless
              ? Math.max(80, Math.floor((canvasHeight - (gridRows + 1) * 16 - 8) / gridRows))
              : 80;
            return (
              <>
              <ResponsiveGridLayout
                className="layout"
                layouts={{ lg: layout, md: layout, sm: layout, xs: layout, xxs: layout }}
                breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                cols={{ lg: 12, md: 12, sm: 12, xs: 12, xxs: 12 }}
                rowHeight={fillRowHeight}
                width={Math.max((canvasWidth > 100 ? canvasWidth : 800) - ((['left_sidebar', 'right_sidebar'].includes(normalizeNavStyle(pageNavStyle)) && savedPages.filter(isPageVisible).length > 1) ? 176 : 0), 320)}
                isDraggable={false}
                isResizable={false}
                margin={[16, 16]}
              >
                {gridVisuals.map(v => {
                  const vizType = v.vizType || report.visualization_type || 'table';
                  const visualDiscussions = discussions.filter(d => d.visual_id === v.id || (!d.visual_id && visuals.length === 1));
                  const vResult = visualResults[v.id] || null;
                  const vDims = v.rowDims || [];
                  const vCol = v.colDim || '';
                  const isCrossFiltered = crossFilter && crossFilter.sourceVisualId !== v.id && crossFilter;
                  const chartData = transformDataForView(vResult?.data, v, config.dateParts || {});
                  const gridGuardNote = isAdmin ? (vResult?.guard_note || vResult?.result?.guard_note || null) : null;
                  return (
                    <div key={v.id} className={`glass-card rounded-2xl p-6 relative overflow-hidden ${isCrossFiltered ? 'ring-1 ring-indigo-500/30' : ''}`} data-visual-id={v.id}>
                      {gridGuardNote && (
                        <div className="flex items-start gap-1.5 mb-2 px-2 py-1 rounded-md bg-amber-500/10 border border-amber-500/25" data-testid={`viewer-guard-note-${v.id}`} title={gridGuardNote}>
                          <ShieldCheck className="w-3 h-3 text-amber-500 mt-0.5 flex-shrink-0" />
                          <p className="text-[10px] leading-snug text-amber-700 dark:text-amber-300">{gridGuardNote}</p>
                        </div>
                      )}
                      {(loadingVisuals.has(v.id) || loadingAiVisuals.has(v.id) || (!resultsReady && !['ai_text','image','rich_text','shape','kpi','forecast','sentinel_summary'].includes(vizType))) && (
                        <div className="absolute inset-0 z-20 bg-background/60 flex items-center justify-center rounded-2xl backdrop-blur-[2px]" data-testid={`visual-loading-${v.id}`}>
                          <div className="flex flex-col items-center gap-2">
                            <Loader2 className={`w-6 h-6 animate-spin ${loadingAiVisuals.has(v.id) ? 'text-indigo-400' : 'text-primary'}`} />
                            <span className={`text-xs font-medium ${loadingAiVisuals.has(v.id) ? 'text-indigo-400' : 'text-muted-foreground'}`}>{loadingAiVisuals.has(v.id) ? 'AI generating...' : 'Loading data...'}</span>
                          </div>
                        </div>
                      )}
                      <div className="flex items-center justify-between mb-4 relative z-30">
                        <p className="text-sm font-heading font-semibold text-foreground">{v.title || report.name}</p>
                        {v.description && <p className="text-[10px] text-muted-foreground mt-0.5">{v.description}</p>}
                        <div className="flex items-center gap-1.5">
                          {!['kpi', 'table', 'pivot', 'ai_text', 'image', 'rich_text', 'shape', 'loss_triangle'].includes(vizType) && vDims.length >= 1 && vDims.length <= 2 && (v.valueMeasures || []).length >= 1 && (
                            <button onClick={() => openForecast(v)} className="flex items-center gap-1 text-[10px] text-indigo-400 hover:text-indigo-300 hover:bg-indigo-500/10 rounded px-1.5 py-0.5" data-testid={`forecast-tile-${v.id}`} title="Forecast future periods">
                              <TrendingUp className="w-3 h-3" /> Forecast
                            </button>
                          )}
                          {vResult && <TileMeasureControls result={vResult} canCertify={canCertify} onCertified={() => refreshAllVisuals(crossFilter, globalFilterValues)} />}
                          {isCrossFiltered && <Badge className="text-[7px] bg-indigo-500/20 text-indigo-400 border-indigo-500/30">filtered</Badge>}
                          {savedVizConfigs[v.id]?.pinnedCorrelation && <button onClick={() => setViewCorrPopup(savedVizConfigs[v.id].pinnedCorrelation)} className="text-emerald-400/60 hover:text-emerald-400 p-0.5 rounded hover:bg-emerald-500/10" title="What Drives This?" data-testid={`view-pinned-corr-${v.id}`}><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-3.5 h-3.5"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg></button>}
                          {vResult && <span className="text-[9px] text-muted-foreground">{vResult.row_count} rows</span>}
                          {vResult?.snapshot?.length > 0 && (
                            <Badge
                              className="text-[8px] bg-primary/15 text-primary border-primary/30 gap-0.5"
                              title={vResult.snapshot[0].defaulted ? 'Auto-defaulted to the latest available snapshot' : 'Pinned to the chosen snapshot date'}
                              data-testid={`tile-as-of-${v.id}`}
                            >
                              <CalendarClock className="w-2.5 h-2.5" /> as of {vResult.snapshot[0].date}{vResult.snapshot[0].defaulted ? ' (latest)' : ''}
                            </Badge>
                          )}
                          {visualDiscussions.length > 0 && (
                            <Badge className="text-[8px] bg-amber-500 text-white border-amber-600 gap-1 cursor-pointer" onClick={() => { setListDiscussions(visualDiscussions); setListOpen(true); }}>
                              <MessageSquare className="w-2.5 h-2.5" /> {visualDiscussions.length}
                            </Badge>
                          )}
                        </div>
                      </div>
                      {(v.filterDims || []).length > 0 && (
                        <div className="flex gap-2 mb-3 flex-wrap">
                          {(v.filterDims || []).map(dim => (
                            <SlicerControl key={dim} dimension={dim} label={resolveLabel(allFields, dim)} values={sortSlicerValues(slicerValues[dim] || [], getSlicerSetting(slicerSettings, dim).sortOrder)} selected={slicerSelections[v.id + '_' + dim]} onChange={val => { const st = getSlicerSetting(slicerSettings, dim); if (st.sync) { setSlicerSelections(p => { const n = { ...p }; getVisuals().forEach(gv => { if ((gv.filterDims || []).includes(dim)) n[gv.id + '_' + dim] = val; }); return n; }); } else { setSlicerSelections(p => ({ ...p, [v.id + '_' + dim]: val })); } if (st.defaultSelection?.type === 'remember') writeRememberedSelection(reportId, dim, val); }} isDate={isDateField(allFields.find(f => f.name === dim))} fiscalStartMonth={fiscalStartMonth} settings={getSlicerSetting(slicerSettings, dim)} />
                          ))}
                        </div>
                      )}
                      {(() => {
                        const chips = [];
                        const fmtSlicerVal = (val) => {
                          if (val && typeof val === 'object' && val.dk) {
                            if (val.dk === 'relative') return String(val.preset).replace(/_/g, ' ');
                            if (val.dk === 'range') return `${val.start} → ${val._end || ''}`;
                            if (val.dk === 'periods') return (val.periods || []).map(p => p.label).join(', ');
                            return '';
                          }
                          return Array.isArray(val) ? val.join(', ') : val;
                        };
                        (v.filterDims || []).forEach(dim => {
                          const val = slicerSelections[v.id + '_' + dim];
                          if (val) chips.push({ key: 'slicer_' + dim, label: resolveLabel(allFields, dim), value: fmtSlicerVal(val),
                            clear: () => setSlicerSelections(p => { const c = { ...p }; delete c[v.id + '_' + dim]; return c; }) });
                        });
                        Object.entries(globalFilterValues).forEach(([field, val]) => {
                          if (!val) return;
                          if (field === '__daterange__' && val.preset) {
                            chips.push({ key: 'global_daterange', label: 'Date range', value: String(val.preset).replace(/_/g, ' '),
                              clear: () => setGlobalFilterValues(p => { const c = { ...p }; delete c['__daterange__']; return c; }) });
                          } else if (field === '__asof__') {
                            chips.push({ key: 'global_asof', label: 'As of', value: val,
                              clear: () => setGlobalFilterValues(p => { const c = { ...p }; delete c['__asof__']; return c; }) });
                          } else if (!field.startsWith('__') && typeof val !== 'object') {
                            chips.push({ key: 'global_' + field, label: resolveLabel(allFields, field), value: val,
                              clear: () => setGlobalFilterValues(p => { const c = { ...p }; delete c[field]; return c; }) });
                          }
                        });
                        if (crossFilter && crossFilter.sourceVisualId !== v.id) {
                          chips.push({ key: 'cross', label: resolveLabel(allFields, crossFilter.field), value: crossFilter.value,
                            clear: () => setCrossFilter(null) });
                        }
                        if (!chips.length) return null;
                        return (
                          <div className="flex gap-1.5 mb-3 flex-wrap relative z-30" data-testid={`active-filter-chips-${v.id}`}>
                            {chips.map(c => (
                              <span key={c.key} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary/10 text-primary text-[10px] border border-primary/20" data-testid={`filter-chip-${v.id}-${c.key}`}>
                                <span className="opacity-70 capitalize">{c.label}:</span>
                                <span className="font-medium">{Array.isArray(c.value) ? c.value.join(', ') : String(c.value).replace('|', ' \u2192 ')}</span>
                                <button onClick={c.clear} className="hover:text-destructive" data-testid={`filter-chip-remove-${v.id}-${c.key}`} aria-label={`Remove ${c.label} filter`}><X className="w-2.5 h-2.5" /></button>
                              </span>
                            ))}
                          </div>
                        );
                      })()}
                      <div className={`relative h-[calc(100%-48px)] ${['table','pivot','loss_triangle','ai_text','rich_text','forecast','sentinel_summary'].includes(vizType) ? 'overflow-auto' : 'overflow-hidden'}`}>
                        {vizType === 'kpi' ? (() => {
                          const vm = v.valueMeasures || [];
                          const val = vResult?.data?.[0]?.[vm[0]] ?? null;
                          const fmtVal = val !== null ? formatValue(val, vm[0]) : '--';
                          const label = resolveLabel(allFields, vm[0]) || 'Value';
                          const indicatorColor = v.kpiIndicatorColor === 'red' ? 'text-red-400' : v.kpiIndicatorColor === 'neutral' ? 'text-muted-foreground' : 'text-emerald-400';
                          const kpiIcons = { dollar: '$', 'trending-up': '\u2197', 'trending-down': '\u2198', users: '\u263A', shield: '\u26E8', chart: '\u2581\u2582\u2583', percent: '%', clock: '\u23F0', star: '\u2605', heart: '\u2665', target: '\u25CE' };
                          return (
                            <div className="flex items-center justify-between h-full px-4">
                              <div className="flex flex-col justify-center">
                                <p className="text-[11px] text-muted-foreground uppercase tracking-wider">{label}</p>
                                <p className="text-3xl font-heading font-bold text-foreground mt-1">{fmtVal}</p>
                                {(() => {
                                  const _f = allFields.find(f => f.name === vm[0]);
                                  const _op = String(_f?.time_intel?.op || '').toLowerCase();
                                  const _isTD = ['ytd', 'qtd', 'mtd', 'itd'].includes(_op)
                                    || /(^|[^a-z])(ytd|qtd|mtd|itd)([^a-z]|$)/i.test(vm[0] || '')
                                    || /(year|quarter|month|inception)[- ]to[- ]date/i.test(vm[0] || '');
                                  return _isTD ? <p className="text-[9px] text-muted-foreground/70 mt-0.5" data-testid={`kpi-as-of-${v.id}`}>as of {v.asOfDate || globalFilterValues['__asof__'] || 'today'}</p> : null;
                                })()}
                                {(() => {
                                  const _pv = vResult?.data?.[0]?.[vm[0] + '__prior'];
                                  const _ppct = vResult?.data?.[0]?.[vm[0] + '__prior_pct'];
                                  if (_pv === undefined || _pv === null) return null;
                                  return <p className={`text-[10px] mt-0.5 ${typeof _ppct === 'number' ? (_ppct >= 0 ? 'text-emerald-400' : 'text-red-400') : 'text-muted-foreground/70'}`} data-testid={`kpi-prior-${v.id}`}>{formatValue(_pv, vm[0])}{typeof _ppct === 'number' ? ` · ${_ppct >= 0 ? '+' : ''}${_ppct}%` : ''} vs same point last year</p>;
                                })()}
                                {v.kpiIndicator && <p className={`text-[11px] mt-2 ${indicatorColor}`}>{v.kpiIndicator}</p>}
                              </div>
                              {v.kpiIcon && v.kpiIcon !== 'none' && (
                                <div className="w-12 h-12 rounded-xl bg-primary/10 border border-primary/20 flex items-center justify-center text-primary text-2xl flex-shrink-0">
                                  {kpiIcons[v.kpiIcon] || ''}
                                </div>
                              )}
                            </div>
                          );
                        })() : vizType === 'ai_text' ? (
                          <div className="p-3">
                            {(viewAiContents[v.id] || v.aiContent || config.aiContents?.[v.id]) ? <div className="text-xs text-foreground/80 whitespace-pre-wrap leading-relaxed">{viewAiContents[v.id] || v.aiContent || config.aiContents?.[v.id]}</div> : <p className="text-xs text-muted-foreground/70 italic">No AI content generated</p>}
                          </div>
                        ) : vizType === 'image' ? (
                          <div className="h-full flex items-center justify-center p-2">
                            {v.imageUrl ? <img src={v.imageUrl} alt={v.title} className={'max-w-full max-h-full ' + (v.imageBorder ? 'border-2 border-border/50 rounded-lg ' : '') + 'object-' + (v.imageFit || 'contain')} /> : null}
                          </div>
                        ) : vizType === 'rich_text' ? (
                          <div className="h-full p-3 overflow-auto">
                            <div style={{ fontSize: v.textSize || 16, color: v.textColor || 'inherit', textAlign: v.textAlign || 'left', fontWeight: v.textBold ? 'bold' : 'normal', fontStyle: v.textItalic ? 'italic' : 'normal', whiteSpace: 'pre-wrap' }}>{v.textContent || ''}</div>
                          </div>
                        ) : vizType === 'shape' ? (
                          <div className="h-full flex items-center justify-center p-2">
                            {(v.shapeType === 'rectangle' || !v.shapeType) && <div className="w-full h-full" style={{ background: v.shapeFill || '#3b82f6', opacity: v.shapeOpacity || 0.3, border: `2px solid ${v.shapeBorder || '#1e40af'}`, borderRadius: v.shapeBorderRadius || 12 }} />}
                            {v.shapeType === 'circle' && <div className="w-full h-full" style={{ background: v.shapeFill || '#3b82f6', opacity: v.shapeOpacity || 0.3, border: `2px solid ${v.shapeBorder || '#1e40af'}`, borderRadius: '50%' }} />}
                            {v.shapeType === 'line_h' && <div className="w-full" style={{ height: 3, background: v.shapeFill || '#3b82f6', opacity: v.shapeOpacity || 1 }} />}
                            {v.shapeType === 'line_v' && <div className="h-full" style={{ width: 3, background: v.shapeFill || '#3b82f6', opacity: v.shapeOpacity || 1 }} />}
                          </div>
                        ) : (visualResults[v.id]?.comparisonResult?.data?.length > 0) ? (
                          <ComparisonTableRenderer result={visualResults[v.id].comparisonResult} vizConfig={savedVizConfigs[v.id] || {}} formatValue={fmtForVisual(v.id)} allFields={allFields} />
                        ) : vizType === 'table' ? (
                          <TableRenderer result={chartData ? { ...vResult, data: chartData } : vResult} vizConfig={{ ...(savedVizConfigs[v.id] || {}), ...(viewVizOverrides[v.id] || {}) }} formatValue={fmtForVisual(v.id)} allFields={allFields} fieldOverrides={savedFieldOverrides} rowDims={vDims} onDrill={(val) => handleCrossFilter(val, v.id, v)} onVizConfigChange={(cfg) => handleTablePref(v.id, cfg)} onRowContextMenu={(row) => { lastHoveredData.current = row; }} />
                        ) : vizType === 'pivot' ? (
                          <PivotTableRenderer pivotResult={visualResults[v.id]?.pivotResult} vizConfig={savedVizConfigs[v.id] || {}} formatValue={fmtForVisual(v.id)} allFields={allFields} onDrill={(val) => handleCrossFilter(val, v.id, v)} />
                        ) : vizType === 'loss_triangle' ? (
                          <LossTriangleRenderer analysis={visualResults[v.id]?.triangleResult} vizConfig={savedVizConfigs[v.id] || {}} formatValue={fmtForVisual(v.id)} />
                        ) : vizType === 'forecast' ? (
                          <ForecastTile snapshot={v.forecastSnapshot} meta={v.forecastMeta} formatValue={fmtForVisual(v.id)} sentinel={v.sentinel} />
                        ) : vizType === 'sentinel_summary' ? (
                          <SentinelSummaryTile />
                        ) : (
                          <ChartRenderer vizType={vizType} data={chartData} rowDims={vDims} colDim={vCol} showGrid={config.showGrid !== false} showLegend={config.showLegend !== false} refLineValue={config.refLineValue || ''} refLineLabel={config.refLineLabel || ''} vizConfig={savedVizConfigs[v.id] || {}} formatValue={fmtForVisual(v.id)} allFields={allFields} fieldOverrides={savedFieldOverrides} onDrill={(val) => handleCrossFilter(val, v.id, v)} secondYAxis={savedSecondYAxis[v.id]} themeColors={savedThemeColors} valueMeasures={v.valueMeasures} discussions={discussions} onHover={(row) => { lastHoveredData.current = row; }} />
                        )}
                        {!['ai_text','kpi','image','rich_text','shape','forecast','sentinel_summary','loss_triangle','pivot'].includes(vizType) && !chartData?.length && !(visualResults[v.id]?.comparisonResult?.data?.length) && !loading && resultsReady && !loadingVisuals.has(v.id) && <p className="text-xs text-muted-foreground text-center py-8">No data available</p>}
                      </div>
                    </div>
                  );
                })}
              </ResponsiveGridLayout>
              {/* Free-positioned object visuals (Image, Text, Shape) */}
              {freeVis.map(v => {
                const fp = v.freePosition || { x: 50, y: 50, w: 200, h: 150 };
                const vizType = v.vizType;
                return (
                  <div
                    key={v.id}
                    className="absolute"
                    style={{ left: fp.x, top: fp.y, width: fp.w, height: fp.h, zIndex: v.zIndex || 10 }}
                    data-visual-id={v.id}
                    data-testid={`free-visual-view-${v.id}`}
                  >
                    {vizType === 'image' ? (
                      <div className="w-full h-full flex items-center justify-center">
                        {v.imageUrl ? <img src={v.imageUrl} alt={v.title} className={'max-w-full max-h-full ' + (v.imageBorder ? 'border-2 border-border/50 rounded-lg ' : '') + 'object-' + (v.imageFit || 'contain')} /> : null}
                      </div>
                    ) : vizType === 'rich_text' ? (
                      <div className="w-full h-full p-2 overflow-auto">
                        <div style={{ fontSize: v.textSize || 16, color: v.textColor || 'inherit', textAlign: v.textAlign || 'left', fontWeight: v.textBold ? 'bold' : 'normal', fontStyle: v.textItalic ? 'italic' : 'normal', whiteSpace: 'pre-wrap' }}>{v.textContent || ''}</div>
                      </div>
                    ) : vizType === 'shape' ? (
                      <div className="w-full h-full">
                        {(v.shapeType === 'rectangle' || !v.shapeType) && <div className="w-full h-full" style={{ background: v.shapeFill || '#3b82f6', opacity: v.shapeOpacity || 0.3, border: `2px solid ${v.shapeBorder || '#1e40af'}`, borderRadius: v.shapeBorderRadius || 12 }} />}
                        {v.shapeType === 'circle' && <div className="w-full h-full" style={{ background: v.shapeFill || '#3b82f6', opacity: v.shapeOpacity || 0.3, border: `2px solid ${v.shapeBorder || '#1e40af'}`, borderRadius: '50%' }} />}
                        {v.shapeType === 'line_h' && <div className="w-full" style={{ height: 3, background: v.shapeFill || '#3b82f6', opacity: v.shapeOpacity || 1, marginTop: '50%' }} />}
                        {v.shapeType === 'line_v' && <div className="h-full" style={{ width: 3, background: v.shapeFill || '#3b82f6', opacity: v.shapeOpacity || 1, marginLeft: '50%' }} />}
                      </div>
                    ) : null}
                  </div>
                );
              })}
              </>
            );
          })()}
          </PageNavChrome>
        </div>
        )}
      </div>

      {printReady && !isReportLayout && <div data-print-ready="1" style={{ display: 'none' }} />}


      {/* Right-click context menu */}
      {contextMenu && (
        <div className="fixed inset-0 z-[100]" onClick={() => setContextMenu(null)} onContextMenu={e => { e.preventDefault(); setContextMenu(null); }}>
          <div className="glass-card rounded-lg shadow-2xl border border-border/50 py-1 w-52" style={{ position: 'fixed', left: contextMenu.x, top: contextMenu.y, zIndex: 101 }} onClick={e => e.stopPropagation()}>
            <button onClick={() => {
              const viz = visuals.find(vv => vv.id === contextMenu.visualId);
              const metric = viz?.valueMeasures?.[0] || '';
              const modelId = report?.model_id || report?.dataset_id || config.model_id || '';
              // Include hovered cell context (dim/value) if present so
              // the investigation is scoped to that slice.
              const cell = lastHoveredData.current || null;
              const filters = {};
              if (cell && viz?.dimensions?.[0]) {
                filters[viz.dimensions[0]] = cell[viz.dimensions[0]];
              }
              setInvestigateTarget({
                metric,
                model_id: modelId,
                filters,
                source: 'report_cell',
                window: 'last 30 days vs prior 30 days',
              });
              setInvestigateOpen(true);
              setContextMenu(null);
            }} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-indigo-300 hover:bg-indigo-500/10 transition-colors" data-testid="ctx-investigate-why">
              <Sparkles className="w-3.5 h-3.5 text-indigo-400" /> Investigate Why
            </button>
            <button onClick={() => startDiscussion(contextMenu.visualId)} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-foreground hover:bg-primary/10 transition-colors" data-testid="ctx-start-discussion">
              <MessageSquare className="w-3.5 h-3.5 text-primary" /> Start a Thread
            </button>
            <button onClick={() => {
              const viz = visuals.find(vv => vv.id === contextMenu.visualId);
              const metric = viz?.valueMeasures?.[0] || '';
              const vr = (visualResults[contextMenu.visualId] || {}).result;
              const rows = vr?.data || [];
              const val = rows[0]?.[metric];
              const name = `Alert: ${resolveLabel(allFields, metric)}`;
              const threshold = val ? String(Math.round(val * 1.1)) : '';
              const modelId = viz?.model_id || report?.query_definition?.model_id || '';
              const params = new URLSearchParams({ create: 'true', metric, name, threshold });
              if (modelId) params.set('model_id', modelId);
              // If the visual is split by a single dimension and resolves to one
              // row, scope the alert to that data point (e.g. LOB = Personal Auto).
              const dim = (viz?.rowDims || [])[0] || (viz?.dimensions || [])[0];
              if (dim && rows.length === 1 && rows[0]?.[dim] != null) {
                params.set('dim', dim); params.set('dimval', String(rows[0][dim]));
              }
              navigate(`/alerts?${params.toString()}`);
              setContextMenu(null);
            }} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-amber-400 hover:bg-amber-500/10 transition-colors" data-testid="ctx-create-alert">
              <Bell className="w-3.5 h-3.5" /> Create Alert
            </button>
            {(() => {
              const viz = visuals.find(vv => vv.id === contextMenu.visualId);
              const calcMetric = (viz?.valueMeasures || []).find(mn => (allFields.find(f => f.name === mn) || {}).is_calculated);
              const modelId = viz?.model_id || report?.query_definition?.model_id || '';
              if (!calcMetric || !modelId) return null;
              return (
                <button onClick={() => { navigate(`/semantic-models?model=${modelId}&measure=${encodeURIComponent(calcMetric)}`); setContextMenu(null); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-foreground hover:bg-primary/10 transition-colors" data-testid="ctx-edit-measure">
                  <Pencil className="w-3.5 h-3.5 text-indigo-400" /> Edit Measure
                </button>
              );
            })()}
            {discussions.filter(d => d.visual_id === contextMenu.visualId || (!d.visual_id && visuals.length === 1)).length > 0 && (
              <button onClick={() => { const filtered = discussions.filter(dd => dd.visual_id === contextMenu.visualId || (!dd.visual_id && visuals.length === 1)); setListDiscussions(filtered); setListOpen(true); setContextMenu(null); }} className="w-full flex items-center gap-2 px-3 py-2 text-xs text-amber-700 dark:text-amber-400 font-medium hover:bg-amber-500/10 transition-colors" data-testid="ctx-view-discussions">
                <MessageSquare className="w-3.5 h-3.5" /> View Discussion Threads ({discussions.filter(d => d.visual_id === contextMenu.visualId || (!d.visual_id && visuals.length === 1)).length})
              </button>
            )}
          </div>
        </div>
      )}

      {/* Right Side Panel */}
      {rightPanelOpen && (
        <div className="absolute right-0 top-0 bottom-0 w-80 border-l border-border/50 z-40 flex flex-col" style={{ background: 'var(--glass-bg)', backdropFilter: 'blur(12px)' }} data-testid="right-panel">
          <div className="flex border-b border-border/50">
            {[['chat', 'Chat'], ['activity', 'Activity'], ['alerts', 'Alerts'], ['details', 'Details']].map(([key, label]) => (
              <button key={key} onClick={() => setRightPanelTab(key)} className={`flex-1 py-2.5 text-[10px] font-semibold uppercase tracking-wider ${rightPanelTab === key ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground'}`}>{label}</button>
            ))}
          </div>
          {rightPanelTab === 'chat' ? (
            <div className="flex-1 min-h-0 p-3"><ObjectChat objectType={assetType} objectId={reportId} label={report?.name || 'Report chat'} /></div>
          ) : (
          <div className="flex-1 overflow-auto p-4 space-y-3">
            {rightPanelTab === 'activity' && (
              <>
                <Button size="sm" className="w-full rounded-lg text-xs" onClick={() => { setDiscussionOpen(true); }}><MessageSquare className="w-3 h-3 mr-1" /> Start Thread</Button>
                {discussions.length === 0 ? <p className="text-xs text-muted-foreground/70 text-center py-6">No threads yet</p> : discussions.map(d => (
                  <div key={d.discussion_id} onClick={() => { setThreadId(d.discussion_id); setThreadOpen(true); }} className="p-3 rounded-xl border border-border/30 hover:border-primary/20 cursor-pointer transition-all">
                    <p className="text-xs font-medium text-foreground">{d.question}</p>
                    <p className="text-[10px] text-muted-foreground mt-1">{d.created_by_name} · {d.replies?.length || 0} replies</p>
                  </div>
                ))}
              </>
            )}
            {rightPanelTab === 'alerts' && (
              <>
                {viewAlerts.filter(a => a.enabled).length === 0 ? <p className="text-xs text-muted-foreground/70 text-center py-6">No active alerts</p> : viewAlerts.filter(a => a.enabled).map(a => (
                  <div key={a.alert_id} className="p-3 rounded-xl border border-border/30">
                    <div className="flex items-center gap-2 mb-1">
                      <Bell className="w-3 h-3 text-amber-400" />
                      <span className="text-xs font-medium text-foreground">{a.name}</span>
                    </div>
                    <p className="text-[10px] text-muted-foreground">{a.metric} {a.operator === 'gt' ? '>' : '<'} {a.threshold}</p>
                    <p className="text-[9px] text-muted-foreground/70 mt-0.5">{a.check_frequency} · {a.severity}</p>
                  </div>
                ))}
              </>
            )}
            {rightPanelTab === 'details' && (
              <>
                <div className="space-y-2">
                  <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold">Report Details</p>
                  <div className="text-xs space-y-1">
                    <div><span className="text-muted-foreground">Name:</span> <span className="text-foreground">{report?.name}</span></div>
                    <div><span className="text-muted-foreground">Model:</span> <span className="text-foreground">{report?.query_definition?.model_id}</span></div>
                    <div><span className="text-muted-foreground">Visuals:</span> <span className="text-foreground">{visuals.length}</span></div>
                    <div><span className="text-muted-foreground">Created:</span> <span className="text-foreground">{report?.created_at ? new Date(report.created_at).toLocaleDateString() : ''}</span></div>
                  </div>
                </div>
                {viewSchedules.filter(s => s.report_id === reportId).length > 0 && (
                  <div className="space-y-2">
                    <p className="text-[9px] text-muted-foreground uppercase tracking-wider font-semibold">Scheduled Deliveries</p>
                    {viewSchedules.filter(s => s.report_id === reportId).map(s => (
                      <div key={s.schedule_id} className="p-2 rounded-lg border border-border/20 text-[10px]">
                        <p className="text-foreground font-medium">{s.name}</p>
                        <p className="text-muted-foreground">{s.frequency} · {(s.recipients || []).length} recipients</p>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          )}
        </div>
      )}

      {/* Dialogs */}
      <ShareViewDialog open={shareViewOpen} onOpenChange={setShareViewOpen} onShare={doShareView} />
      <StartDiscussionDialog open={discussionOpen} onOpenChange={setDiscussionOpen} reportId={reportId} visualId={discussionVisualId} dataContext={discussionCtx} screenshotData={discussionScreenshot} reportState={captureReportState()} onCreated={disc => { setDiscussions(prev => [disc, ...prev]); setThreadId(disc.discussion_id); setThreadOpen(true); }} />
      <DiscussionThreadDialog open={threadOpen} onOpenChange={setThreadOpen} discussionId={threadId} />
      <DiscussionListDialog open={listOpen} onOpenChange={setListOpen} discussions={listDiscussions} onSelect={id => { setThreadId(id); setThreadOpen(true); }} />
      <Sheet open={usageOpen} onOpenChange={setUsageOpen}>
        <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="report-usage-sheet">
          <SheetHeader className="mb-4">
            <SheetTitle>Usage & activity</SheetTitle>
            <SheetDescription>Views, edits, and top viewers for this {assetType}.</SheetDescription>
          </SheetHeader>
          {usageOpen && reportId && <AssetUsageTab objectType={assetType} objectId={reportId} title="" />}
        </SheetContent>
      </Sheet>      {/* Pinned Correlation Popup (View Mode) */}
      {viewCorrPopup && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-background/50 backdrop-blur-sm" onClick={() => setViewCorrPopup(null)}>
          <div className="glass-card border-border/50 rounded-2xl p-6 max-w-lg w-full max-h-[80vh] overflow-auto shadow-2xl" onClick={e => e.stopPropagation()} data-testid="view-correlation-popup">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold text-foreground flex items-center gap-2"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4 text-emerald-400"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg> What Drives This Metric?</h3>
              <button onClick={() => setViewCorrPopup(null)} className="text-muted-foreground hover:text-foreground"><svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg></button>
            </div>
            <div className="space-y-3">
              <div className="p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                <p className="text-xs text-foreground/80">{viewCorrPopup.summary}</p>
              </div>
              {(viewCorrPopup.drivers || []).map((d, i) => (
                <div key={i} className="p-2.5 rounded-lg border border-border/30 text-[10px]">
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-semibold text-foreground">{resolveLabel(allFields, d.field) || d.field?.replace(/_/g, ' ')}</span>
                    <span className={`text-[8px] font-bold ${d.correlation?.includes('positive') ? 'text-sky-400' : 'text-rose-400'}`}>{d.correlation}</span>
                  </div>
                  <p className="text-foreground/70">{d.insight}</p>
                </div>
              ))}
              {viewCorrPopup.recommendation && (
                <div className="p-2.5 rounded-lg bg-primary/5 border border-primary/20 text-[10px]">
                  <span className="text-primary font-semibold">Recommendation:</span>
                  <span className="text-foreground/80 ml-1">{viewCorrPopup.recommendation}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Forecast dialog (Insights) */}
      <Dialog open={!!fcDialog} onOpenChange={(o) => { if (!o) setFcDialog(null); }}>
        <DialogContent className="max-w-3xl" data-testid="forecast-dialog">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><TrendingUp className="w-4 h-4 text-indigo-500" /> Forecast — {fcDialog?.title}</DialogTitle></DialogHeader>
          {fcDialog?.forecast
            ? <ForecastChart forecast={fcDialog.forecast} formatValue={(x) => (typeof x === 'number' ? x.toLocaleString() : x)} />
            : <div className="flex items-center justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-indigo-500" /></div>}
        </DialogContent>
      </Dialog>

      {/* Share preview dialog */}      <SharePreviewDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        resourceType="report"
        resourceId={reportId}
        resourceName={report?.name}
      />

      <InvestigatePanel open={investigateOpen} onOpenChange={setInvestigateOpen} target={investigateTarget} />

      <ExportScheduleDialog open={scheduleOpen} onOpenChange={setScheduleOpen} resourceId={reportId} resourceName={report?.name} resourceType={assetType} />

      <ExportFrameworkDialog
        open={frameworkExportOpen} onOpenChange={setFrameworkExportOpen}
        mode={assetType} resourceId={reportId} resourceName={report?.name} resourceType={assetType}
        getContext={buildExportContext}
        nativeReportPdf={isReportLayout}
        onNativePdf={handleExportPdf}
        exporting={exporting}
        exportProgress={exportProgress}
        onCancelExport={cancelExport}
      />

      <ReportSourceComparison reportId={reportId} open={compareOpen} onOpenChange={setCompareOpen} />

      <Dialog open={exportPickerOpen} onOpenChange={(v) => { if (!exporting) setExportPickerOpen(v); }}>
        <DialogContent className="max-w-sm" data-testid="export-range-dialog">
          <DialogHeader><DialogTitle>Export Insight</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <p className="text-[11px] text-muted-foreground mb-1.5">Format</p>
              <div className="space-y-1.5">
                {[['pdf', 'PDF Document', FileText], ['png', 'PNG Image', ImageIcon]].map(([val, label, Icon]) => (
                  <label key={val} className={`flex items-center gap-2.5 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${exportFormat === val ? 'border-primary/50 bg-primary/10' : 'border-border/40 hover:bg-muted/30'} ${exporting ? 'opacity-50 pointer-events-none' : ''}`} data-testid={`export-format-${val}`}>
                    <input type="radio" name="export-format" value={val} checked={exportFormat === val} onChange={() => setExportFormat(val)} className="accent-primary" data-testid={`export-format-radio-${val}`} />
                    <Icon className="w-4 h-4 text-muted-foreground" />
                    <span className="text-sm text-foreground">{label}</span>
                  </label>
                ))}
              </div>
            </div>
            {exportFormat === 'pdf' && isReportLayout && rangeTotal > 1 && (
              <div data-testid="export-page-range">
                <p className="text-[11px] text-muted-foreground mb-1.5">Pages ({rangeTotal} total)</p>
                <div className="flex items-center gap-2">
                  <input type="number" min={1} max={rangeTotal} value={rangeFrom}
                    onChange={e => setRangeFrom(Math.max(1, Math.min(rangeTotal, Number(e.target.value) || 1)))}
                    className="w-20 rounded-lg border border-border/50 bg-muted/40 px-2 py-1 text-sm" data-testid="export-range-from" />
                  <span className="text-sm text-muted-foreground">to</span>
                  <input type="number" min={1} max={rangeTotal} value={rangeTo}
                    onChange={e => setRangeTo(Math.max(1, Math.min(rangeTotal, Number(e.target.value) || 1)))}
                    className="w-20 rounded-lg border border-border/50 bg-muted/40 px-2 py-1 text-sm" data-testid="export-range-to" />
                </div>
              </div>
            )}
            {exportFormat === 'pdf' && !isReportLayout && (report?.config?.pages || []).length > 1 && (
              <div>
                <p className="text-[11px] text-muted-foreground mb-1">Pages to include</p>
                {(report?.config?.pages || []).map(pg => {
                  const checked = (exportPageIds || []).includes(pg.id);
                  return (
                    <label key={pg.id} className="flex items-center gap-2 text-sm cursor-pointer py-0.5" data-testid={`export-page-${pg.id}`}>
                      <input type="checkbox" checked={checked} disabled={exporting} onChange={(e) => {
                        setExportPageIds(prev => e.target.checked ? [...(prev || []), pg.id] : (prev || []).filter(x => x !== pg.id));
                      }} />
                      {pg.name}
                    </label>
                  );
                })}
              </div>
            )}
            {exporting ? (
              <div className="space-y-2" data-testid="export-progress">
                <div className="flex items-center justify-between text-[11px] text-muted-foreground">
                  <span data-testid="export-progress-label">{exportProgress.label || 'Exporting…'}</span>
                  {exportProgress.total > 1 && <span data-testid="export-progress-count">{Math.min(exportProgress.current + 1, exportProgress.total)}/{exportProgress.total}</span>}
                </div>
                <Progress value={exportProgress.total ? Math.round((exportProgress.current / exportProgress.total) * 100) : 12} className={exportProgress.total ? '' : 'animate-pulse'} data-testid="export-progress-bar" />
                <Button variant="outline" onClick={cancelExport} className="w-full rounded-lg text-sm" data-testid="export-cancel-btn">
                  <X className="w-4 h-4 mr-1" /> Cancel Export
                </Button>
              </div>
            ) : (
              <Button onClick={runExport} disabled={exportFormat === 'pdf' && (report?.config?.pages || []).length > 1 && !(exportPageIds || []).length} className="w-full rounded-lg text-sm" data-testid="export-range-confirm">
                <Download className="w-4 h-4 mr-1" /> Export {exportFormat.toUpperCase()}
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>
      {slowQuery && (loadingVisuals.size > 0 || loadingAiVisuals.size > 0) && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 flex items-center gap-3 px-4 py-2.5 rounded-full bg-background/95 border border-border/60 shadow-lg backdrop-blur print:hidden" data-testid="report-slow-query-pill">
          <Loader2 className="w-4 h-4 text-primary animate-spin" />
          <span className="text-xs text-muted-foreground">This query is taking longer than usual…</span>
          <Button variant="outline" size="sm" onClick={cancelRunningQuery} className="h-7 rounded-full gap-1.5 text-xs" data-testid="cancel-query-btn">
            <X className="w-3.5 h-3.5" /> Cancel
          </Button>
        </div>
      )}
    </div>
  );
}
